// Shine Agri • self-contained page, scoped design and source-aware public feeds.
// Uses the existing project dependencies and ../../firebaseConfig.

// nafed-update/src/pages/nafed/NafedHomePage.jsx
import React, { useEffect as useEffect2, useMemo as useMemo2, useRef, useState as useState2 } from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signOut
} from "firebase/auth";
import {
  collection as collection2,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot as onSnapshot2,
  orderBy,
  query,
  where,
  setDoc,
  serverTimestamp
} from "firebase/firestore";
import { auth, db as db2 } from "../../firebaseConfig";

// nafed-update/src/pages/tender/TenderPublicationData.js
import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../firebaseConfig";

// nafed-update/src/pages/tender/TenderData.js
var tenderDateMillis = (value) => {
  if (value == null || value === "") return NaN;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (typeof value.seconds === "number") return value.seconds * 1e3;
  if (typeof value === "number" || value instanceof Date) return new Date(value).getTime();
  const text = String(value).trim();
  return Date.parse(/^\d{4}-\d\d-\d\dT\d\d:\d\d(?::\d\d(?:\.\d+)?)?$/.test(text) ? `${text}+05:30` : text);
};

// nafed-update/src/pages/tender/TenderPublicationData.js
function procurementChannelOf(item = {}) {
  if (item.procurementChannel === "DIRECT" || item.procurementChannel === "NAFED") return item.procurementChannel;
  return item.nafedProject === true || item.projectSource === "NAFED" || item.applicationPortal === "NAFED_PACS_APPLY" ? "NAFED" : "DIRECT";
}
var publicationText = (value) => typeof value === "string" ? value.trim() : value == null ? "" : String(value).trim();
var publicationUpper = (value) => publicationText(value).toUpperCase();
var publicationIds = (values) => [...new Set(values.flat(Infinity).filter((value) => typeof value === "string").map((value) => value.trim()).filter(Boolean))];
var removedPublicationStatuses = /* @__PURE__ */ new Set(["DELETED", "REMOVED", "ARCHIVED", "TRASHED", "CANCELLED", "CANCELED", "SOFT_DELETED", "PURGED"]);
var unavailablePublicationStatuses = /* @__PURE__ */ new Set(["DRAFT", "UNPUBLISHED", "WITHDRAWN", "PRIVATE", "HIDDEN"]);
var isRemovedPublication = (item) => item.isDeleted === true || item.deleted === true || item.isArchived === true || item.archived === true || Boolean(item.deletedAt || item.archivedAt || item.removedAt || item.cancelledAt) || [item.status, item.tenderStatus, item.publicationStatus, item.recordStatus].some((value) => removedPublicationStatuses.has(publicationUpper(value)));
function actualPublishedNotice(item, source = item.sourceCollection || "tenders") {
  if (!item || isRemovedPublication(item) || ["PRIVATE", "HIDDEN", "INTERNAL"].includes(publicationUpper(item.visibility)) || [item.isPublished, item.published, item.publicVisible].some((value) => value === false)) return false;
  const veto = /* @__PURE__ */ new Set([...removedPublicationStatuses, ...unavailablePublicationStatuses, "INTERNAL"]);
  if ([item.status, item.tenderStatus, item.publishStatus, item.publicationStatus, item.statusFlag, item.finalStatus].some((value) => veto.has(publicationUpper(value)))) return false;
  const flags = [item.isPublished, item.published, item.publicVisible, item.visibility, item.statusFlag, item.publishStatus, item.finalStatus];
  if (flags.some((value) => value === true || ["PUBLIC", "PUBLISHED"].includes(publicationUpper(value)))) return true;
  if (flags.some((value) => value !== void 0 && value !== null && value !== "")) return false;
  return source === "nafed_tenders" && ["PUBLISHED", "OPEN", "LIVE", "BID_OPEN", "AWARDED", "CLOSED", "COMPLETED"].includes(publicationUpper(item.status || item.tenderStatus));
}
var publicationNumber = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) && value >= 0 ? value : null;
  const text = String(value).trim().toUpperCase().replace(/,/g, "");
  const match = text.match(/[-+]?(?:\d+\.?\d*|\.\d+)/);
  if (!match) return null;
  const parsed = Number(match[0]);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  const multiplier = /(?:CRORES?|CR)\b/.test(text) ? 1e7 : /MMT\b/.test(text) ? 1e6 : /(?:LMT|LAKHS?|LACS?)\b/.test(text) ? 1e5 : /KMT\b/.test(text) ? 1e3 : 1;
  return parsed * multiplier;
};
function noticeCapacity(item) {
  const aggregate = publicationNumber(item.capacityMt ?? item.capacityMT ?? item.capacity ?? item.storageCapacityMt ?? item.totalCapacityMt);
  if (aggregate > 0) return aggregate;
  const units = Array.isArray(item.tenderUnits) ? item.tenderUnits : [];
  const sum = units.reduce((total, unit) => total + (publicationNumber(unit.capacityMt ?? unit.capacityMT ?? unit.capacity ?? unit.storageCapacityMt) || 0), 0);
  return sum > 0 ? sum : aggregate;
}
function noticeReferences(item = {}) {
  const units = Array.isArray(item.tenderUnits) ? item.tenderUnits : [];
  return {
    projects: publicationIds([item.sourceProjectIds || [], item.linkedWorkflowProjectIds || [], item.sourceProjectId || "", item.projectIds || [], item.projectId || "", item.godownProjectId || "", item.nafedProjectId || "", units.map((unit) => [unit.sourceProjectIds || [], unit.sourceProjectId || "", unit.projectIds || [], unit.projectId || "", unit.godownProjectId || ""])]),
    applications: publicationIds([item.sourceApplicationIds || [], item.linkedApplicationIds || [], item.sourceApplicationId || "", item.applicationIds || [], item.applicationId || "", units.map((unit) => [unit.sourceApplicationIds || [], unit.sourceApplicationId || "", unit.applicationIds || [], unit.applicationId || ""])]),
    pacs: publicationIds([item.sourcePacsId || "", item.sourcePacsIds || [], units.map((unit) => [unit.sourcePacsId || "", unit.sourcePacsIds || []])])
  };
}
function normaliseNafedPublicTender(item = {}, id = "") {
  const sourceCollection = publicationText(item.sourceCollection) || "nafed_tenders";
  if (item.procurementChannel === "DIRECT") return null;
  if (sourceCollection !== "nafed_tenders" && procurementChannelOf(item) !== "NAFED") return null;
  if (!actualPublishedNotice(item, sourceCollection)) return null;
  const canonicalId = publicationText(item.canonicalId || item.sourceTenderId || item.tenderId || item.sourceId || id);
  if (!canonicalId) return null;
  const shared = sourceCollection === "tenders";
  const references = noticeReferences(item);
  const sharedHref = `/tender/${encodeURIComponent(canonicalId)}?channel=NAFED`;
  const legacyHref = `/nafed/tenders/${encodeURIComponent(canonicalId)}`;
  const providedHref = publicationText(item.detailsHref || item.detailHref || item.detailUrl);
  const detailsHref = shared ? sharedHref : sourceCollection === "nafed_tenders" && (!providedHref || providedHref === legacyHref) ? legacyHref : null;
  const applyHref = shared ? `/tender/apply?tid=${encodeURIComponent(canonicalId)}&channel=NAFED` : null;
  const unitNames = (Array.isArray(item.tenderUnits) ? item.tenderUnits : []).map((unit) => unit.pacsName || unit.societyName || unit.unitTitle).filter(Boolean);
  return {
    ...item,
    id: publicationText(id) || `${sourceCollection}:${canonicalId}`,
    canonicalId,
    sourceTenderId: canonicalId,
    sourceId: canonicalId,
    sourceCollection,
    procurementChannel: "NAFED",
    isPublished: true,
    isDeleted: false,
    legacyNotice: !shared,
    sourceType: shared ? "shared" : sourceCollection === "nafed_tenders" ? "legacy" : "unknown",
    detailsHref,
    detailHref: detailsHref,
    href: detailsHref,
    applyHref,
    tenderNo: publicationText(item.tenderNo || item.tenderNumber || item.noticeNo || canonicalId),
    title: publicationText(item.title || item.tenderTitle || item.workName || item.societyName || item.pacsName) || "Published tender notice",
    societyName: publicationText(item.societyName || item.pacsName || item.ownerCompany || unitNames.join(" · ")),
    pacsName: publicationText(item.pacsName || item.societyName || item.ownerCompany || unitNames[0]),
    state: publicationText(item.state || item.stateName) || "National",
    district: publicationText(item.district || item.districtName || item.city),
    capacity: noticeCapacity(item),
    capacityMt: noticeCapacity(item),
    value: publicationNumber(item.tenderValue ?? item.estimatedCost ?? item.projectCost ?? item.baseAmount ?? item.amount),
    startAt: item.startAt ?? item.openDate ?? item.openingDate ?? item.bidStartDate ?? null,
    closingDate: item.closingDate ?? item.endAt ?? item.closeDate ?? item.bidEndDate ?? item.lastDate ?? null,
    status: publicationUpper(item.status || item.tenderStatus) || "PUBLISHED",
    actor: publicationText(item.actor || item.generatedByPmcName || item.publishedByOrganization),
    sourceProjectIds: references.projects,
    sourceApplicationIds: references.applications,
    sourcePacsIds: references.pacs,
    createdAt: item.createdAt || item.publishedAt || item.updatedAt || null
  };
}
function normaliseUpcomingProjection(item, id) {
  if (item?.schemaVersion !== 1 || item.procurementChannel !== "NAFED" || item.visibility !== "PUBLIC" || item.isDeleted !== false || item.stage !== "UPCOMING" || isRemovedPublication(item)) return null;
  const name = [item.societyName, item.pacsName].find((value) => typeof value === "string" && value.trim())?.trim() || "";
  const sourceId = typeof item.sourcePacsId === "string" ? item.sourcePacsId.trim() : "";
  if (!name || !sourceId || !Array.isArray(item.sourceProjectIds) || !Array.isArray(item.sourceApplicationIds)) return null;
  return { id: publicationText(id), name, state: publicationText(item.state), district: publicationText(item.district), capacityMt: publicationNumber(item.capacityMt), stage: "UPCOMING", sourceId, sourceProjectIds: publicationIds([item.sourceProjectIds || []]), sourceApplicationIds: publicationIds([item.sourceApplicationIds || []]), _updated: tenderDateMillis(item.updatedAt) || 0 };
}
function selectUpcomingPacs(projections = [], publishedNotices = []) {
  const notices = publishedNotices.filter((item) => actualPublishedNotice(item, item.sourceCollection || "tenders"));
  const published = { projects: /* @__PURE__ */ new Set(), applications: /* @__PURE__ */ new Set(), pacs: /* @__PURE__ */ new Set() };
  notices.forEach((item) => {
    const refs = noticeReferences(item);
    Object.keys(published).forEach((kind) => refs[kind].forEach((value) => published[kind].add(value)));
  });
  const rows = projections.map((item) => normaliseUpcomingProjection(item, item.id)).filter(Boolean).sort((a, b) => b._updated - a._updated);
  const parents = rows.map((_, index) => index);
  const find = (index) => {
    while (parents[index] !== index) {
      parents[index] = parents[parents[index]];
      index = parents[index];
    }
    return index;
  };
  const owner = /* @__PURE__ */ new Map();
  rows.forEach((row, index) => {
    const identities = [`pacs:${row.sourceId}`, ...row.sourceProjectIds.map((value) => `project:${value}`), ...row.sourceApplicationIds.map((value) => `application:${value}`)];
    identities.forEach((identity) => {
      if (owner.has(identity)) {
        const a = find(index), b = find(owner.get(identity));
        parents[Math.max(a, b)] = Math.min(a, b);
      } else owner.set(identity, index);
    });
  });
  const grouped = /* @__PURE__ */ new Map();
  rows.forEach((row, index) => {
    const root = find(index);
    if (!grouped.has(root)) grouped.set(root, []);
    grouped.get(root).push(row);
  });
  const groups = [...grouped.values()].map((group) => ({ ...group[0], sourceProjectIds: publicationIds(group.map((row) => row.sourceProjectIds)), sourceApplicationIds: publicationIds(group.map((row) => row.sourceApplicationIds)), _sources: publicationIds(group.map((row) => row.sourceId)) }));
  return groups.filter((row) => !row._sources.some((value) => published.pacs.has(value)) && !row.sourceProjectIds.some((value) => published.projects.has(value)) && !row.sourceApplicationIds.some((value) => published.applications.has(value))).sort((a, b) => a.name.localeCompare(b.name, "en-IN") || a.id.localeCompare(b.id)).map(({ _updated, _sources, ...row }) => row);
}
function useNafedUpcomingPacs(publishedNotices = []) {
  const [records, setRecords] = useState([]);
  const [upcomingState, setUpcomingState] = useState("loading");
  useEffect(() => {
    let active = true;
    let unsubscribe;
    try {
      unsubscribe = onSnapshot(collection(db, "nafed_public_upcoming_pacs"), (snapshot) => {
        if (!active) return;
        const values = [];
        snapshot.forEach((document) => values.push({ ...document.data(), id: document.id }));
        setRecords(values);
        setUpcomingState("live");
      }, () => {
        if (active) {
          setRecords([]);
          setUpcomingState("error");
        }
      });
    } catch {
      setRecords([]);
      setUpcomingState("unavailable");
    }
    return () => {
      active = false;
      unsubscribe?.();
    };
  }, []);
  const upcomingPacs = useMemo(() => selectUpcomingPacs(records, publishedNotices), [records, publishedNotices]);
  return { upcomingPacs, upcomingState };
}

// nafed-update/src/pages/nafed/NafedHomePage.jsx
var HERO_IMAGE = "/assets/nafed/anna-bhandaran-hero.png";
var NAFED_LOGO = "/images/nafed-logo.png";
var parseMetricNumber = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (value === null || value === void 0 || value === "") return 0;
  const text = String(value).trim().toUpperCase().replaceAll(",", "");
  const number = Number.parseFloat(text.replace(/[^0-9.+-]/g, ""));
  if (!Number.isFinite(number)) return 0;
  if (text.includes("CRORE") || /\bCR\b/.test(text)) return number * 1e7;
  if (text.includes("LMT") || text.includes("LAKH")) return number * 1e5;
  if (text.includes("MMT")) return number * 1e6;
  if (text.includes("KMT")) return number * 1e3;
  return number;
};
var publicCount = (...values) => {
  for (const value of values) {
    if (value !== null && value !== void 0 && value !== "") {
      const parsed = Math.max(0, Math.trunc(parseMetricNumber(value)));
      if (parsed > 0) return parsed;
    }
  }
  return 0;
};
var firstMetric = (...values) => {
  let zeroFound = false;
  for (const value of values) {
    if (value !== null && value !== void 0 && value !== "") {
      const parsed = parseMetricNumber(value);
      if (parsed > 0) return parsed;
      if (parsed === 0) zeroFound = true;
    }
  }
  return zeroFound ? 0 : 0;
};
var CAPACITY_FIELDS = [
  "capacityMt",
  "capacityMT",
  "capacityInMt",
  "capacityInMT",
  "totalCapacityMt",
  "totalCapacityMT",
  "requestedCapacityMt",
  "requestedCapacityMT",
  "plannedCapacityMt",
  "plannedCapacityMT",
  "proposedCapacityMt",
  "proposedCapacityMT",
  "projectCapacityMt",
  "proposedCapacity",
  "projectCapacityMT",
  "approvedCapacityMt",
  "approvedCapacityMT",
  "sanctionedCapacityMt",
  "sanctionedCapacityMT",
  "godownCapacityMt",
  "sanctionedCapacity",
  "dprCapacityMt",
  "dprCapacityMT",
  "godownCapacityMT",
  "storageCapacityMt",
  "storageCapacityMT",
  "capacityTonnes",
  "capacityTons",
  "storageCapacity",
  "godownCapacity",
  "selectedCapacity",
  "capacityValue",
  "projectCapacity",
  "capacity"
];
var capacityFrom = (item = {}) => {
  const sources = [
    item,
    item.projectDetails,
    item.applicationData,
    item.application,
    item.formData,
    item.form,
    item.details,
    item.projectForm,
    item.godownApplication,
    item.applicationDetails,
    item.godownDetails,
    item.storageDetails,
    item.technicalDetails,
    item.basicDetails,
    item.siteDetails,
    item.proposal,
    item.projectData,
    item.project
  ].filter((source) => source && typeof source === "object");
  for (const source of sources) {
    for (const field of CAPACITY_FIELDS) {
      const raw = source[field];
      const resolved = raw && typeof raw === "object" ? firstMetric(
        raw.value,
        raw.mt,
        raw.capacityMt,
        raw.capacityMT,
        raw.capacity,
        raw.label
      ) : parseMetricNumber(raw);
      if (resolved > 0) return resolved;
    }
  }
  return 0;
};
var REMOVED_STATUSES = /* @__PURE__ */ new Set([
  "DELETED",
  "REMOVED",
  "ARCHIVED",
  "TRASHED",
  "CANCELLED"
]);
var cleanActivityTitle = (value = "") => String(value || "").replace(/\bNAFED\s+record\s*[-:]\s*/gi, "").replace(/\bstatus\s+updated\s+to\s+/gi, "approved as ").replace(/\bPACS_WORK_START_APPROVED\b/gi, "PACS work start approved").replace(/\bPACS WORK START APPROVED\b/gi, "PACS work start approved").replace(/\s+/g, " ").trim();
var activityTypeLabel = (value = "") => {
  const type = String(value || "UPDATE").trim().toUpperCase();
  const labels = {
    PACS_WORK_START_APPROVED: "WORK START",
    NAFED_PACS_APPLICATION_ARCHIVED: "ARCHIVED",
    NAFED_TENDER_PUBLISHED_TO_ALL_INDIA_PORTAL: "TENDER",
    PMC_DOCUMENT_APPROVED: "DOCUMENT",
    PMC_DOCUMENT_QUERY: "QUERY",
    PMC_DOCUMENT_REJECTED: "REJECTED",
    REGISTRATION: "PACS REG.",
    APPROVAL: "APPROVAL",
    PROJECT: "PROJECT",
    TENDER: "TENDER",
    STATE_UPDATE: "STATE"
  };
  return labels[type] || type.replaceAll("_", " ").slice(0, 14);
};
var HERO_SLIDES = [
  {
    id: "integrated-platform",
    image: HERO_IMAGE,
    kicker: "PRADHAN MANTRI ANNA BHANDARAN YOJANA",
    title: "One Nation. One Storage Network.",
    description: "Building the world’s largest cooperative grain storage network through PACS—digitally monitored from registration to completion."
  },
  {
    id: "modern-godown",
    image: "/assets/nafed/slider-modern-godown.jpg",
    kicker: "MODERN STORAGE INFRASTRUCTURE",
    title: "Modern Godown Infrastructure",
    description: "Scientific and secure grain storage facilities for PACS and farmers."
  },
  {
    id: "project-monitoring",
    image: "/assets/nafed/slider-project-monitoring.jpg",
    kicker: "DIGITAL PROJECT MONITORING",
    title: "Transparent Project Monitoring",
    description: "State-wise monitoring from application to project completion."
  }
];
var DEFAULT_STATE_STATS = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry"
].map((state) => ({ state, pacs: 0, projects: 0, capacityMt: 0 }));
var ROLE_OPTIONS = [
  ["HEAD_OFFICE", "NAFED Head Office", "🏛️"],
  ["STATE_BRANCH", "State Branch", "🏢"],
  ["STATE_REGISTRAR", "State Registrar", "🏛️"],
  ["DISTRICT_REGISTRAR", "District Registrar", "🏛️"],
  ["BANK", "Bank", "🏦"],
  ["PMC", "PMC Agency", "👷"],
  ["PACS", "PACS / Society", "🌾"],
  ["SURVEY_VENDOR", "Site Survey Vendor", "📍"],
  ["SOIL_TEST_VENDOR", "Soil Test Vendor", "🧪"],
  ["DESIGN_VENDOR", "Drawing & Design Vendor", "📐"],
  ["ESTIMATE_VENDOR", "Estimate & BOQ Vendor", "🧮"],
  ["DPR_VENDOR", "DPR Consultant / Vendor", "📘"]
];
var ROLE_ROUTES = {
  HEAD_OFFICE: "/nafed/head",
  NAFED_HEAD: "/nafed/head",
  STATE_BRANCH: "/nafed/branch",
  STATE_REGISTRAR: "/nafed/state-registrar",
  STATE_REG: "/nafed/state-registrar",
  DISTRICT_REGISTRAR: "/nafed/district",
  DISTRICT_REG: "/nafed/district",
  PMC: "/nafed/pmc",
  PACS: "/nafed/pacs",
  SURVEY_VENDOR: "/nafed/vendor",
  SOIL_TEST_VENDOR: "/nafed/vendor",
  DESIGN_VENDOR: "/nafed/vendor",
  ESTIMATE_VENDOR: "/nafed/vendor",
  DPR_VENDOR: "/nafed/vendor",
  BANK: "/agency/dashboard"
};
var REGISTERABLE_ROLES = ROLE_OPTIONS;
var ROLE_REGISTRATION_CONFIG = {
  HEAD_OFFICE: {
    state: false,
    district: false,
    taluka: false,
    village: false,
    address: false,
    scope: "NATIONAL"
  },
  STATE_BRANCH: {
    state: true,
    district: false,
    taluka: false,
    village: false,
    address: false,
    scope: "STATE"
  },
  STATE_REGISTRAR: {
    state: true,
    district: false,
    taluka: false,
    village: false,
    address: false,
    scope: "STATE"
  },
  PMC: {
    state: true,
    district: false,
    taluka: false,
    village: false,
    address: false,
    scope: "STATE"
  },
  DISTRICT_REGISTRAR: {
    state: true,
    district: true,
    taluka: false,
    village: false,
    address: false,
    scope: "DISTRICT"
  },
  PACS: {
    state: true,
    district: true,
    taluka: true,
    village: true,
    address: true,
    scope: "PACS_LOCAL"
  },
  BANK: {
    state: true,
    district: true,
    taluka: false,
    village: false,
    address: true,
    scope: "DISTRICT"
  },
  SURVEY_VENDOR: {
    state: false,
    district: false,
    taluka: false,
    village: false,
    address: true,
    scope: "NATIONAL_TECHNICAL_VENDOR"
  },
  SOIL_TEST_VENDOR: {
    state: false,
    district: false,
    taluka: false,
    village: false,
    address: true,
    scope: "NATIONAL_TECHNICAL_VENDOR"
  },
  DESIGN_VENDOR: {
    state: false,
    district: false,
    taluka: false,
    village: false,
    address: true,
    scope: "NATIONAL_TECHNICAL_VENDOR"
  },
  ESTIMATE_VENDOR: {
    state: false,
    district: false,
    taluka: false,
    village: false,
    address: true,
    scope: "NATIONAL_TECHNICAL_VENDOR"
  },
  DPR_VENDOR: {
    state: false,
    district: false,
    taluka: false,
    village: false,
    address: true,
    scope: "NATIONAL_TECHNICAL_VENDOR"
  }
};
var ROLE_APPROVAL_CONFIG = {
  HEAD_OFFICE: {
    approverRole: "SHINE_AGRI_ADMIN",
    approverLabel: "Shine Agri Super Administrator"
  },
  STATE_BRANCH: {
    approverRole: "HEAD_OFFICE",
    approverLabel: "NAFED Head Office"
  },
  STATE_REGISTRAR: {
    approverRole: "STATE_BRANCH",
    approverLabel: "NAFED State Branch"
  },
  DISTRICT_REGISTRAR: {
    approverRole: "STATE_REGISTRAR",
    approverLabel: "State Registrar"
  },
  BANK: { approverRole: "STATE_BRANCH", approverLabel: "NAFED State Branch" },
  PMC: { approverRole: "STATE_BRANCH", approverLabel: "NAFED State Branch" },
  PACS: { approverRole: "STATE_BRANCH", approverLabel: "NAFED State Branch" },
  SURVEY_VENDOR: {
    approverRole: "HEAD_OFFICE",
    approverLabel: "NAFED Head Office"
  },
  SOIL_TEST_VENDOR: {
    approverRole: "HEAD_OFFICE",
    approverLabel: "NAFED Head Office"
  },
  DESIGN_VENDOR: {
    approverRole: "HEAD_OFFICE",
    approverLabel: "NAFED Head Office"
  },
  ESTIMATE_VENDOR: {
    approverRole: "HEAD_OFFICE",
    approverLabel: "NAFED Head Office"
  },
  DPR_VENDOR: {
    approverRole: "HEAD_OFFICE",
    approverLabel: "NAFED Head Office"
  }
};
var ROLE_PENDING_STATUS = {
  HEAD_OFFICE: "PENDING_SHINE_AGRI",
  STATE_BRANCH: "PENDING_HEAD_OFFICE_APPROVAL",
  STATE_REGISTRAR: "PENDING_STATE_BRANCH_APPROVAL",
  DISTRICT_REGISTRAR: "PENDING_STATE_REGISTRAR",
  BANK: "PENDING_STATE_BRANCH_APPROVAL",
  PMC: "PENDING_STATE_BRANCH_APPROVAL",
  PACS: "PENDING_STATE_BRANCH_APPROVAL",
  SURVEY_VENDOR: "PENDING_HEAD_OFFICE_APPROVAL",
  SOIL_TEST_VENDOR: "PENDING_HEAD_OFFICE_APPROVAL",
  DESIGN_VENDOR: "PENDING_HEAD_OFFICE_APPROVAL",
  ESTIMATE_VENDOR: "PENDING_HEAD_OFFICE_APPROVAL",
  DPR_VENDOR: "PENDING_HEAD_OFFICE_APPROVAL"
};
var ROLE_IDENTITY_CONFIG = {
  HEAD_OFFICE: {
    organization: "NAFED Office / Division",
    registration: "Employee / Authorisation ID",
    designation: "Official Designation"
  },
  STATE_BRANCH: {
    organization: "NAFED State Branch Name",
    registration: "Branch / Office Code",
    designation: "Official Designation"
  },
  STATE_REGISTRAR: {
    organization: "Registrar Office Name",
    registration: "Office / Employee ID",
    designation: "Official Designation"
  },
  DISTRICT_REGISTRAR: {
    organization: "District Registrar Office",
    registration: "Office / Employee ID",
    designation: "Official Designation"
  },
  BANK: {
    organization: "Bank and Branch Name",
    registration: "RBI / Branch / IFSC Reference",
    designation: "Authorised Officer Designation"
  },
  PMC: {
    organization: "PMC Agency / Company Name",
    registration: "Company Registration Number",
    designation: "Authorised Representative Designation"
  },
  PACS: {
    organization: "PACS / Society Name",
    registration: "Society Registration Number",
    designation: "Chairman / Secretary Designation"
  },
  SURVEY_VENDOR: {
    organization: "Survey Firm / Agency Name",
    registration: "GST / Company / Professional Registration",
    designation: "Authorised Surveyor"
  },
  SOIL_TEST_VENDOR: {
    organization: "Soil Testing Laboratory Name",
    registration: "NABL / GST / Laboratory Registration",
    designation: "Authorised Laboratory Officer"
  },
  DESIGN_VENDOR: {
    organization: "Architectural / Structural Design Firm",
    registration: "COA / Engineer / Company Registration",
    designation: "Authorised Design Engineer"
  },
  ESTIMATE_VENDOR: {
    organization: "Estimate / Quantity Survey Firm",
    registration: "GST / Professional Registration",
    designation: "Quantity Surveyor / Estimator"
  },
  DPR_VENDOR: {
    organization: "DPR Consultancy Name",
    registration: "GST / Company / Professional Registration",
    designation: "Authorised DPR Consultant"
  }
};
var GUJARAT_DISTRICTS = [
  "Ahmedabad",
  "Amreli",
  "Anand",
  "Aravalli",
  "Banaskantha",
  "Bharuch",
  "Bhavnagar",
  "Botad",
  "Chhota Udaipur",
  "Dahod",
  "Dang",
  "Devbhoomi Dwarka",
  "Gandhinagar",
  "Gir Somnath",
  "Jamnagar",
  "Junagadh",
  "Kheda",
  "Kutch",
  "Mahisagar",
  "Mahesana",
  "Morbi",
  "Narmada",
  "Navsari",
  "Panchmahal",
  "Patan",
  "Porbandar",
  "Rajkot",
  "Sabarkantha",
  "Surat",
  "Surendranagar",
  "Tapi",
  "Vadodara",
  "Valsad"
];
var mapKey = (value) => String(value || "").toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]/g, "");
function IndiaLiveMap({ states, selectedState, onSelect }) {
  const mapRef = useRef(null);
  const [svgMarkup, setSvgMarkup] = useState2("");
  useEffect2(() => {
    let active = true;
    fetch("/assets/nafed/india-states-map.svg").then((response) => {
      if (!response.ok) throw new Error("India map asset not found");
      return response.text();
    }).then((source) => {
      if (!active) return;
      const parser = new DOMParser();
      const documentNode = parser.parseFromString(source, "image/svg+xml");
      documentNode.querySelectorAll("style").forEach((node) => node.remove());
      const maximum = Math.max(
        1,
        ...states.map(
          (item) => Number(item.capacityMt || item.projects || item.pacs || 0)
        )
      );
      const stateLookup = new Map(
        states.map((item) => [mapKey(item.state || item.name), item])
      );
      documentNode.querySelectorAll("path").forEach((path) => {
        const name = path.getAttribute("aria-label") || "";
        const live = stateLookup.get(mapKey(name));
        const value = Number(
          live?.capacityMt || live?.projects || live?.pacs || 0
        );
        const intensity = Math.min(1, value / maximum);
        path.setAttribute("data-state", name);
        path.setAttribute("tabindex", "0");
        path.setAttribute("role", "button");
        path.setAttribute(
          "fill",
          value > 0 ? `hsl(145 52% ${72 - intensity * 39}%)` : "#dce8e0"
        );
        path.setAttribute("stroke", "#ffffff");
        path.setAttribute("stroke-width", "1.15");
        path.style.cursor = "pointer";
        if (mapKey(name) === mapKey(selectedState?.state)) {
          path.setAttribute("fill", "#f0a428");
          path.setAttribute("stroke", "#062653");
          path.setAttribute("stroke-width", "2.4");
        }
      });
      const svg = documentNode.documentElement;
      svg.setAttribute("class", "mp-india-svg");
      svg.setAttribute("aria-label", "Interactive live India programme map");
      setSvgMarkup(new XMLSerializer().serializeToString(svg));
    }).catch((error) => console.error("India map loading failed:", error));
    return () => {
      active = false;
    };
  }, [states, selectedState]);
  useEffect2(() => {
    const node = mapRef.current;
    if (!node) return void 0;
    const chooseState = (event) => {
      const path = event.target.closest?.("path[data-state]");
      if (!path || !node.contains(path)) return;
      const name = path.getAttribute("data-state");
      const record = states.find((item) => mapKey(item.state || item.name) === mapKey(name)) || { state: name, pacs: 0, projects: 0, capacityMt: 0 };
      onSelect(record);
    };
    const handleKeyDown = (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      if (!event.target.closest?.("path[data-state]")) return;
      event.preventDefault();
      chooseState(event);
    };
    node.addEventListener("click", chooseState);
    node.addEventListener("keydown", handleKeyDown);
    return () => {
      node.removeEventListener("click", chooseState);
      node.removeEventListener("keydown", handleKeyDown);
    };
  }, [svgMarkup, states, onSelect]);
  return <div
    ref={mapRef}
    className="mp-live-india-map"
    dangerouslySetInnerHTML={{
      __html: svgMarkup || '<div class="mp-map-loading">Loading live map…</div>'
    }}
  />;
}
function PortalIcon({ name = "building", size = 20, ...props }) {
  const paths = {
    arrow: ["M5 12h14M13 6l6 6-6 6"],
    chevron: ["m9 5 7 7-7 7"],
    check: ["m5 12 4 4L19 6"],
    close: ["m6 6 12 12M6 18 18 6"],
    menu: ["M4 6h16M4 12h16M4 18h16"],
    shield: ["M12 3 4 6v6c0 5 8 9 8 9s8-4 8-9V6l-8-3Z", "m8 12 3 3 5-6"],
    lock: ["M7 10V7a5 5 0 0 1 10 0v3M5 10h14v11H5V10ZM12 14v3"],
    user: ["M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0ZM4 21v-2a8 8 0 0 1 16 0v2"],
    users: ["M15 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0ZM6 21v-2a6 6 0 0 1 12 0v2M18 4a3 3 0 0 1 0 6M21 20v-2a5 5 0 0 0-3-4M6 4a3 3 0 0 0 0 6M3 20v-2a5 5 0 0 1 3-4"],
    building: ["M3 21h18M5 21V7l7-4 7 4v14M9 21v-5h6v5M8 9h1m6 0h1M8 12h1m6 0h1"],
    bank: ["m3 8 9-5 9 5H3ZM5 10v8m5-8v8m4-8v8m5-8v8M3 21h18"],
    storage: ["M3 21V8l9-5 9 5v13H3ZM7 21V11h10v10M7 15h10M7 18h10"],
    map: ["m3 6 6-3 6 3 6-3v15l-6 3-6-3-6 3V6ZM9 3v15M15 6v15"],
    branch: ["M12 3v5M5 13v-2h14v2M12 8v5M3 15h4v6H3v-6ZM10 15h4v6h-4v-6ZM17 15h4v6h-4v-6Z"],
    file: ["M14 3H5v18h14V8l-5-5ZM14 3v5h5M8 12h8M8 16h8"],
    clock: ["M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM12 7v5l3 2"],
    search: ["M16 10a6 6 0 1 1-12 0 6 6 0 0 1 12 0Zm-1 5 6 6"],
    location: ["M20 10c0 6-8 11-8 11S4 16 4 10a8 8 0 1 1 16 0ZM15 10a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"],
    phone: ["M5 3h4l2 5-3 2a16 16 0 0 0 6 6l2-3 5 2v4a2 2 0 0 1-2 2C10 21 3 14 3 5a2 2 0 0 1 2-2Z"],
    help: ["M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM9 9a3 3 0 0 1 6 0c0 2-3 2-3 4M12 17h.01"],
    play: ["m9 6 9 6-9 6V6Z"],
    pause: ["M8 5v14M16 5v14"],
    chart: ["M3 21h18M6 17v-5M12 17V7M18 17V3"]
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" {...props}>
      {(paths[name] || paths.building).map((d, index) => <path key={index} d={d} />)}
    </svg>;
}
var roleIconName = (roleValue) => ({
  HEAD_OFFICE: "bank",
  STATE_BRANCH: "branch",
  STATE_REGISTRAR: "bank",
  DISTRICT_REGISTRAR: "bank",
  BANK: "bank",
  PACS: "storage",
  PMC: "building",
  SURVEY_VENDOR: "location",
  SOIL_TEST_VENDOR: "chart",
  DESIGN_VENDOR: "file",
  ESTIMATE_VENDOR: "chart",
  DPR_VENDOR: "file"
})[roleValue] || "user";
var MISSION_PORTAL_STYLES = `
.mission-portal{--mp-navy:#0f2042;--mp-green:#249030;--mp-ink:#172943;--mp-muted:#60708a;--mp-line:#dfe6ef;--mp-bg:#f4f7fb;min-height:100vh;background:var(--mp-bg);color:var(--mp-ink);font-family:Inter,"Segoe UI",Arial,sans-serif;font-size:14px;line-height:1.55;color-scheme:light}
.mission-portal *,.mission-portal *::before,.mission-portal *::after{box-sizing:border-box}
.mission-portal :where(h1,h2,h3,h4,p){margin:0}
.mission-portal :where(button,input,select,textarea){font:inherit}
.mission-portal :where(a,button){-webkit-tap-highlight-color:transparent}
.mission-portal :where(a,button,input,select,[tabindex]):focus-visible{outline:3px solid #6b99ca;outline-offset:3px}
.mission-portal button{cursor:pointer}
.mission-portal button:disabled{cursor:not-allowed;opacity:.5}
.mission-portal a{color:inherit}
.mission-portal .mp-wrap{width:100%;max-width:1440px;margin:0 auto;padding-left:32px;padding-right:32px;min-width:0}
.mission-portal .mp-skip{position:absolute;left:20px;top:-100px;z-index:1000;padding:10px 16px;border-radius:5px;background:white;color:var(--mp-navy);box-shadow:0 4px 20px #0002}
.mission-portal .mp-skip:focus{top:8px}
.mission-portal .mp-top{background:#eaf0f7;border-bottom:1px solid var(--mp-line);font-size:12px;color:#526783}
.mission-portal .mp-top-inner{min-height:32px;display:flex;align-items:center;justify-content:space-between;gap:14px}
.mission-portal .mp-top-right,.mission-portal .mp-connection{display:flex;align-items:center;gap:8px}
.mission-portal .mp-top-right{gap:20px}
.mission-portal .mp-top a{text-decoration:none}
.mission-portal .mp-top a:hover{text-decoration:underline;text-underline-offset:3px}
.mission-portal .mp-live-dot{width:7px;height:7px;flex:none;background:#8290a5;border-radius:50%}
.mission-portal .mp-connection.is-live .mp-live-dot{background:var(--mp-green)}
.mission-portal .mp-header{background:#fff}
.mission-portal .mp-header-inner{min-height:94px;display:flex;align-items:center;justify-content:space-between;gap:28px;padding-top:13px;padding-bottom:13px}
.mission-portal .mp-brand{display:flex;align-items:center;gap:18px;min-width:0;text-decoration:none}
.mission-portal .mp-logo{width:83px;height:64px;object-fit:contain;flex:none;border-right:1px solid var(--mp-line);padding-right:17px}
.mission-portal .mp-brand-title{color:var(--mp-navy);font-size:23px;font-weight:750;line-height:1.2;letter-spacing:-.025em}
.mission-portal .mp-brand-title strong{font-weight:800}
.mission-portal .mp-brand p{font-size:13px;color:var(--mp-muted);margin-top:6px}
.mission-portal .mp-header-actions{display:flex;align-items:center;gap:10px;flex:none}
.mission-portal .mp-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;min-height:42px;border:1px solid #6d809e;border-radius:5px;padding:10px 17px;background:#fff;color:var(--mp-navy);font-size:14px;font-weight:650;line-height:1.4;text-decoration:none;box-shadow:none;transition:background-color .15s,border-color .15s}
.mission-portal .mp-btn.primary{background:var(--mp-green);border-color:var(--mp-green);color:white}
.mission-portal .mp-btn:hover{background:#edf2f8;border-color:var(--mp-navy)}
.mission-portal .mp-btn.primary:hover{background:#1c7727;border-color:#1c7727}
.mission-portal .mp-nav{background:var(--mp-navy);color:white}
.mission-portal .mp-nav-inner{display:flex;align-items:center;justify-content:space-between;gap:20px;position:relative}
.mission-portal .mp-nav-items{display:flex;align-items:stretch;min-width:0;gap:3px}
.mission-portal .mp-nav-items>a{display:inline-flex;align-items:center;justify-content:center;gap:7px;min-height:48px;padding:11px 16px;border-bottom:3px solid transparent;color:#e7edf7;font-size:13px;font-weight:550;line-height:1.35;text-decoration:none;white-space:nowrap}
.mission-portal .mp-nav-items>a:hover,.mission-portal .mp-nav-items>a[aria-current=page]{background:#ffffff0d;border-bottom-color:#66bb70;color:white}
.mission-portal .mp-nav-status{display:inline-flex;align-items:center;gap:7px;white-space:nowrap;color:#c4d2e6;font-size:12px}
.mission-portal .mp-nav-toggle{display:none;align-items:center;gap:9px;min-height:48px;padding:10px 0;background:transparent;border:0;color:white;font-weight:650}
.mission-portal .mp-hero{background:#fff;border-bottom:1px solid var(--mp-line)}
.mission-portal .mp-hero-inner{display:grid;grid-template-columns:minmax(0,.93fr) minmax(0,1.07fr);gap:40px;align-items:center;padding-top:42px;padding-bottom:42px}
.mission-portal .mp-scheme{display:flex;align-items:center;gap:8px;color:#237e30;font-size:12px;font-weight:750;line-height:1.5;letter-spacing:.065em;text-transform:uppercase;margin-bottom:15px}
.mission-portal .mp-hero h1{color:var(--mp-navy);font-size:clamp(36px,3.6vw,52px);font-weight:780;line-height:1.12;letter-spacing:-.045em;margin-bottom:18px}
.mission-portal .mp-hero h1 span{display:block;color:var(--mp-green)}
.mission-portal .mp-hero-description{font-size:16px;line-height:1.75;color:var(--mp-muted);max-width:530px}
.mission-portal .mp-hero-actions{display:flex;align-items:center;gap:11px;flex-wrap:wrap;margin-top:25px}
.mission-portal .mp-trust{display:flex;align-items:center;gap:13px 20px;flex-wrap:wrap;margin-top:24px;padding-top:19px;border-top:1px solid #e7ecf2;color:#556b65;font-size:12px}
.mission-portal .mp-trust span{display:inline-flex;align-items:center;gap:6px}
.mission-portal .mp-trust svg{color:var(--mp-green);flex:none}
.mission-portal .mp-hero-visual{min-width:0;margin:0;background:#edf3ef;border:1px solid #dde7e0;border-radius:10px;overflow:hidden}
.mission-portal .mp-hero-image{display:block;width:100%;height:auto;aspect-ratio:1.6;object-fit:contain;object-position:center}
.mission-portal .mp-hero-caption{display:flex;align-items:center;gap:8px;padding:11px 15px;background:#f5f9f6;border-top:1px solid #dde7e0;color:#526a5c;font-size:12px}
.mission-portal .mp-hero-caption svg{color:var(--mp-green);flex:none}
.mission-portal .mp-content{padding-top:26px;padding-bottom:32px}
.mission-portal .mp-section-heading{display:flex;justify-content:space-between;align-items:flex-start;gap:20px;margin-bottom:17px}
.mission-portal .mp-eyebrow{font-size:12px;font-weight:700;letter-spacing:.065em;text-transform:uppercase;color:#237e30;margin-bottom:5px}
.mission-portal .mp-section-heading h2{color:var(--mp-navy);font-size:25px;font-weight:750;line-height:1.3;letter-spacing:-.025em}
.mission-portal .mp-section-heading p{color:var(--mp-muted);font-size:14px;margin-top:6px;line-height:1.6}
.mission-portal .mp-section-heading .mp-connection{flex:none;margin-top:9px;font-size:12px;color:var(--mp-muted)}
.mission-portal .mp-kpis{display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:12px;margin-bottom:24px}
.mission-portal .mp-kpi{padding:18px 17px;background:white;border:1px solid var(--mp-line);border-radius:8px;min-width:0}
.mission-portal .mp-kpi-top{display:flex;align-items:flex-start;gap:8px;color:#5a6e89;font-size:12px;min-height:35px;line-height:1.45}
.mission-portal .mp-kpi-top svg{color:var(--mp-green);flex:none;margin-top:1px}
.mission-portal .mp-kpi-value{display:block;font-size:clamp(23px,2vw,29px);font-weight:750;letter-spacing:-.035em;line-height:1.2;color:var(--mp-navy);margin:9px 0 6px;overflow-wrap:anywhere;font-variant-numeric:tabular-nums}
.mission-portal .mp-kpi small{display:block;color:#728199;font-size:12px;line-height:1.5}
.mission-portal .mp-ticker{display:flex;align-items:center;gap:16px;min-height:45px;padding:9px 15px;margin-bottom:24px;border:1px solid #d5e2d8;border-radius:6px;background:#edf6ef;color:#526b5a}
.mission-portal .mp-ticker-label{display:inline-flex;align-items:center;gap:7px;flex:none;color:#237a31;font-size:12px;font-weight:750}
.mission-portal .mp-ticker-track{min-width:0;flex:1;overflow:hidden;white-space:nowrap}
.mission-portal .mp-ticker-runner{display:inline-flex;align-items:center;gap:48px;min-width:100%;animation:mpActivityScroll 55s linear infinite}
.mission-portal .mp-ticker-item{display:inline-flex;align-items:center;gap:9px;font-size:13px;white-space:nowrap}
.mission-portal .mp-ticker-item b{color:#347b43;font-size:12px;font-weight:600}
.mission-portal .mp-ticker-track:hover .mp-ticker-runner,.mission-portal .mp-ticker-track:focus-within .mp-ticker-runner,.mission-portal .mp-ticker.is-paused .mp-ticker-runner{animation-play-state:paused}
@keyframes mpActivityScroll{0%,8%{transform:translateX(0)}100%{transform:translateX(-70%)}}
.mission-portal .mp-ticker-controls{display:flex;align-items:center;gap:10px;flex:none}
.mission-portal .mp-ticker-pause{display:grid;place-items:center;width:29px;height:29px;border:1px solid #c6d8cb;border-radius:4px;color:#376645;background:#fff}
.mission-portal .mp-text-link{display:inline-flex;align-items:center;gap:5px;font-size:13px;font-weight:650;color:#2f5c93;text-decoration:none;white-space:nowrap}
.mission-portal .mp-text-link:hover{text-decoration:underline;text-underline-offset:3px}
.mission-portal .mp-grid{display:grid;grid-template-columns:minmax(0,1.15fr) minmax(0,1fr);gap:20px;margin-bottom:22px;align-items:stretch}
.mission-portal .mp-panel{min-width:0;background:#fff;border:1px solid var(--mp-line);border-radius:8px;overflow:hidden;box-shadow:none}
.mission-portal .mp-panel-head{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;min-height:72px;padding:18px 21px 15px;border-bottom:1px solid #e7ecf2}
.mission-portal .mp-panel-head h2,.mission-portal .mp-panel-head h3{color:var(--mp-navy);font-size:17px;font-weight:700;line-height:1.4;letter-spacing:-.015em}
.mission-portal .mp-panel-head p{font-size:12px;line-height:1.6;color:var(--mp-muted);margin-top:4px}
.mission-portal .mp-panel-head>.mp-text-link{margin-top:3px;font-size:12px}
.mission-portal .mp-overview{display:grid;grid-template-columns:minmax(0,1.05fr) minmax(0,1fr);gap:16px;padding:19px;align-items:start}
.mission-portal .mp-map{position:relative;min-width:0;align-self:stretch;display:flex;flex-direction:column;justify-content:center;background:#f8fbf8;border:1px solid #edf2ed;border-radius:6px}
.mission-portal .mp-live-india-map{width:100%;height:330px;display:grid;place-items:center;padding:8px}
.mission-portal .mp-live-india-map .mp-india-svg{width:100%;height:100%;overflow:visible}
.mission-portal .mp-live-india-map path{transition:fill .15s,stroke .15s}
.mission-portal .mp-live-india-map path:hover,.mission-portal .mp-live-india-map path:focus{fill:#e8b657!important;stroke:#0f2042!important;stroke-width:2.2!important;outline:none}
.mission-portal .mp-map-loading{font-size:13px;color:var(--mp-muted)}
.mission-portal .mp-map>small{display:block;padding:9px 10px;text-align:center;border-top:1px solid #e5ede6;color:#687e6e;font-size:12px;line-height:1.5}
.mission-portal .mp-state-list{min-width:0}
.mission-portal .mp-state-list>h3{font-size:12px;font-weight:650;color:#6a7b91;margin:1px 0 9px}
.mission-portal .mp-state-row{display:grid;grid-template-columns:20px minmax(0,1fr) auto;gap:7px;align-items:start;width:100%;background:#fff;color:#455d7b;text-align:left;padding:10px 2px;border:0;border-bottom:1px solid #e8edf3;font-size:12px;line-height:1.5}
.mission-portal .mp-state-row:hover,.mission-portal .mp-state-row[aria-pressed=true]{background:#f0f7f1}
.mission-portal .mp-state-row>span:first-child{color:#8b99ac}
.mission-portal .mp-state-row b{font-size:12px;font-weight:650;color:#237e30;text-align:right;font-variant-numeric:tabular-nums}
.mission-portal .mp-state-detail{margin-top:17px;padding:13px;border:1px solid #d6e6d9;border-radius:5px;background:#f2f8f3}
.mission-portal .mp-state-detail h3{font-size:14px;font-weight:700;line-height:1.45;color:#267336;margin:0 0 11px}
.mission-portal .mp-state-metrics{display:flex;flex-direction:column;gap:7px}
.mission-portal .mp-state-metric{display:flex;justify-content:space-between;align-items:flex-start;gap:8px;font-size:12px;line-height:1.5}
.mission-portal .mp-state-metric span{color:#66816c}
.mission-portal .mp-state-metric b{font-size:13px;font-weight:700;color:var(--mp-navy);text-align:right;font-variant-numeric:tabular-nums}
.mission-portal .mp-activity{padding:0 21px}
.mission-portal .mp-activity-row{display:grid;grid-template-columns:30px minmax(0,1fr);gap:11px;padding:15px 0;border-bottom:1px solid #e8edf3;align-items:start}
.mission-portal .mp-activity-row:last-child{border:0}
.mission-portal .mp-activity-icon{width:29px;height:29px;display:grid;place-items:center;border:1px solid #d5e6d8;border-radius:50%;background:#f1f8f2;color:#348340;flex:none}
.mission-portal .mp-activity-top{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:4px}
.mission-portal .mp-type{font-size:11px;font-weight:700;color:#347c40;letter-spacing:.025em;overflow-wrap:anywhere}
.mission-portal .mp-activity-top time{font-size:12px;color:#7a8ba1;text-align:right;flex:none}
.mission-portal .mp-activity-text{font-size:14px;line-height:1.55;color:#2d4465;overflow-wrap:anywhere}
.mission-portal .mp-activity-meta{display:flex;align-items:center;gap:5px 10px;flex-wrap:wrap;font-size:12px;line-height:1.5;color:#75869c;margin-top:5px}
.mission-portal .mp-auto{display:flex;align-items:center;gap:8px;padding:12px 21px;border-top:1px solid #e7ecf2;background:#fbfcfe;color:#70839d;font-size:12px}
.mission-portal .mp-empty{padding:35px 22px;text-align:center;color:var(--mp-muted);font-size:14px;line-height:1.7}
.mission-portal .mp-empty>svg{display:block;margin:0 auto 10px;color:#8c9daf}
.mission-portal .mp-empty strong{display:block;font-size:15px;color:#3f5979;margin-bottom:5px}
.mission-portal .mp-pipeline-panel{margin-bottom:24px}
.mission-portal .mp-pipeline{padding:21px}
.mission-portal .mp-stage-row{display:grid;grid-template-columns:repeat(9,minmax(0,1fr));gap:12px}
.mission-portal .mp-stage{min-width:0;position:relative;padding-right:6px;border-right:1px solid #e6ecf2}
.mission-portal .mp-stage:last-child{border:0;padding-right:0}
.mission-portal .mp-stage-number{display:inline-grid;place-items:center;width:26px;height:26px;margin-bottom:10px;border-radius:50%;background:#edf5ee;border:1px solid #d7e7d9;color:#367d40;font-size:11px;font-weight:700}
.mission-portal .mp-stage-label{display:block;min-height:38px;color:#64768f;font-size:12px;line-height:1.5}
.mission-portal .mp-stage b{display:block;margin-top:6px;font-size:25px;line-height:1.3;font-weight:750;letter-spacing:-.03em;color:var(--mp-navy);font-variant-numeric:tabular-nums}
.mission-portal .mp-progress{height:4px;background:#e8edf2;margin-top:10px;overflow:hidden;border-radius:2px}
.mission-portal .mp-progress i{display:block;height:100%;background:#49a052}
.mission-portal .mp-pipeline-total{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;margin-top:20px;padding-top:15px;border-top:1px solid #e8edf3;font-size:12px;color:#74839a}
.mission-portal .mp-pipeline-total b{color:#3c577a;font-weight:650;margin-left:7px}
.mission-portal .mp-opportunities{margin-bottom:24px;scroll-margin-top:18px}
.mission-portal .mp-opportunity-tabs{display:flex;gap:23px;border-bottom:1px solid var(--mp-line);padding:0 21px;background:#fff}
.mission-portal .mp-tab{display:inline-flex;align-items:center;justify-content:center;gap:8px;padding:14px 4px 12px;min-height:47px;border:0;border-bottom:3px solid transparent;border-radius:0;background:transparent;color:#71819a;font-size:14px;font-weight:600;text-align:left}
.mission-portal .mp-tab[aria-selected=true]{border-bottom-color:var(--mp-green);color:var(--mp-navy);font-weight:700}
.mission-portal .mp-tab-count{padding:1px 7px;min-width:23px;border-radius:4px;background:#edf2f7;color:#60758f;font-size:12px;text-align:center}
.mission-portal .mp-table-scroll{width:100%;overflow-x:auto;overscroll-behavior-x:contain}
.mission-portal .mp-tender-table{width:100%;border-collapse:collapse;min-width:900px;text-align:left;font-size:13px;line-height:1.55}
.mission-portal .mp-tender-table th{padding:11px 18px;background:#edf2f8;color:#425c7e;font-size:11px;font-weight:750;letter-spacing:.03em;text-transform:uppercase;border-bottom:1px solid var(--mp-line);text-align:left;white-space:nowrap}
.mission-portal .mp-tender-table td{padding:15px 18px;border-bottom:1px solid #e8edf3;color:#556c89;vertical-align:top;overflow-wrap:anywhere}
.mission-portal .mp-tender-table tr:last-child td{border-bottom:0}
.mission-portal .mp-tender-table tbody tr:hover{background:#fafcfe}
.mission-portal .mp-tender-table .mp-tender-name{min-width:240px;max-width:420px;color:var(--mp-navy);font-weight:650}
.mission-portal .mp-tender-table .mp-notice-id{color:#70829c;font-size:12px;max-width:190px}
.mission-portal .mp-live-status,.mission-portal .mp-upcoming-badge{display:inline-flex;align-items:center;justify-content:center;padding:4px 8px;border:1px solid #d8e1ec;border-radius:4px;background:#f1f4f8;color:#526b8c;font-size:11px;line-height:1.4;font-weight:650;white-space:normal;text-align:center}
.mission-portal .mp-upcoming-badge{background:#fff8e6;border-color:#ecdeb8;color:#8a6a21}
.mission-portal .mp-notice-pending{display:block;margin-top:5px;color:#7c8ba0;font-size:12px}
.mission-portal .mp-table-footer{display:flex;align-items:center;justify-content:space-between;gap:14px;flex-wrap:wrap;padding:13px 21px;border-top:1px solid var(--mp-line);background:#fbfcfe;color:#7b8ba1;font-size:12px}
.mission-portal .mp-upcoming-tools{display:flex;align-items:center;justify-content:space-between;gap:18px;padding:16px 21px;border-bottom:1px solid #e7ecf2}
.mission-portal .mp-upcoming-tools p{font-size:12px;line-height:1.6;color:var(--mp-muted);max-width:660px}
.mission-portal .mp-search{position:relative;display:flex;align-items:center;max-width:320px;width:100%;min-width:220px}
.mission-portal .mp-search>svg{position:absolute;left:12px;pointer-events:none;color:#7186a1}
.mission-portal .mp-search input{width:100%;height:40px;padding:9px 12px 9px 39px;border:1px solid #cedae7;border-radius:5px;background:white;color:var(--mp-navy);font-size:13px;outline-offset:1px}
.mission-portal .mp-search input::placeholder{color:#8291a5;opacity:1}
.mission-portal .mp-upcoming-table{min-width:740px}
.mission-portal .mp-upcoming-table .mp-tender-name{min-width:220px}
.mission-portal .mp-pagination{display:flex;align-items:center;gap:10px}
.mission-portal .mp-pagination button{display:inline-flex;align-items:center;gap:4px;min-height:33px;padding:6px 10px;border:1px solid #ced9e6;border-radius:4px;background:white;color:#445e80;font-size:12px}
.mission-portal .mp-pagination button:first-child svg{transform:rotate(180deg)}
.mission-portal .mp-lower{display:grid;grid-template-columns:minmax(0,1.15fr) minmax(0,1fr);gap:20px;align-items:start}
.mission-portal .mp-videos{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px;padding:20px}
.mission-portal .mp-video-card{min-width:0}
.mission-portal .mp-video-media{display:flex;align-items:center;justify-content:center;aspect-ratio:16/10;position:relative;overflow:hidden;border:1px solid #d9e3ed;border-radius:5px;background:#eaf0f6;color:#8094ae}
.mission-portal .mp-video-media img{display:block;width:100%;height:100%;object-fit:cover}
.mission-portal .mp-play{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:white;text-decoration:none;background:#0f204226}
.mission-portal .mp-play>span{display:grid;place-items:center;width:40px;height:40px;border-radius:50%;background:#0f2042d9;border:1px solid #ffffffb3}
.mission-portal .mp-video-card h3{font-size:13px;font-weight:650;line-height:1.5;color:#314d72;margin-top:10px;overflow-wrap:anywhere}
.mission-portal .mp-video-card p{color:#7c8da5;font-size:12px;line-height:1.5;margin-top:5px}
.mission-portal .mp-quick-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:0;padding:5px 20px}
.mission-portal .mp-quick{display:flex;align-items:center;gap:11px;min-height:65px;text-decoration:none;padding:12px 8px;border-bottom:1px solid #e8edf3;color:#465e7c;font-size:13px;line-height:1.4}
.mission-portal .mp-quick:nth-last-child(-n+2){border-bottom:0}
.mission-portal .mp-quick>svg{color:#3b8948;flex:none}
.mission-portal .mp-quick:hover{background:#f7faf8;color:#237e30}
.mission-portal .mp-footer{margin-top:0;padding:20px 0;background:var(--mp-navy);color:#b7c8df;border-top:3px solid #249030}
.mission-portal .mp-footer-inner{display:flex;align-items:center;justify-content:space-between;gap:24px}
.mission-portal .mp-footer strong{display:block;color:#edf3fb;font-size:13px;font-weight:650;margin-bottom:3px}
.mission-portal .mp-footer p{font-size:12px;line-height:1.7}
.mission-portal .mp-footer-links{display:flex;gap:20px;flex-wrap:wrap;font-size:12px}
.mission-portal .mp-footer-links a{text-decoration:none}
.mission-portal .mp-footer-links a:hover{text-decoration:underline;text-underline-offset:3px}
/* Authentication keeps the existing handlers and fields, with scoped presentation. */
.nafed-page>.overlay{padding:24px;background:#08162bd1;backdrop-filter:blur(3px);font-family:Inter,"Segoe UI",Arial,sans-serif;font-size:14px;color:#172943}
.nafed-page>.overlay .modal{width:min(600px,100%);max-height:calc(100dvh - 48px);border:1px solid #dfe6ef;border-radius:10px;box-shadow:0 24px 80px #0003}
.nafed-page>.overlay .reg-modal{width:min(850px,100%)}
.nafed-page>.overlay .modal-head{padding:22px 25px;background:#0f2042;border-radius:9px 9px 0 0;align-items:flex-start}
.nafed-page>.overlay .modal-head h2{font-size:23px;font-weight:700;line-height:1.3;margin:7px 0 0;letter-spacing:-.025em}
.nafed-page>.overlay .modal-close{border-radius:5px;width:34px;height:34px;flex:none;background:#ffffff12;font-size:24px;line-height:1}
.nafed-page>.overlay .modal-close:hover{background:#ffffff24}
.nafed-page>.overlay .modal-body{padding:25px}
.nafed-page>.overlay .role-badge{width:50px;height:50px;border:1px solid #d7e8da;background:#eff7f0;color:#249030;margin:0 auto 13px;font-size:22px}
.nafed-page>.overlay .modal-title{font-size:20px;font-weight:700;line-height:1.35;color:#0f2042;margin-bottom:22px}
.nafed-page>.overlay .login-role-grid{grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}
.nafed-page>.overlay .login-role-option{min-height:76px;padding:12px;border-radius:6px;grid-template-columns:34px minmax(0,1fr) auto;background:#fbfcfe;border-color:#dfe6ef;box-shadow:none}
.nafed-page>.overlay .login-role-option:hover{transform:none;border-color:#81b68a;background:#f3f9f4;box-shadow:none}
.nafed-page>.overlay .login-role-option span{width:34px;height:34px;border-radius:6px;background:#edf6ef;color:#2c8839}
.nafed-page>.overlay .login-role-option b{font-size:13px;font-weight:650;line-height:1.4;color:#233f65}
.nafed-page>.overlay .login-role-option i{font-size:12px;color:#7c8ea7;display:flex;align-items:center}
.nafed-page>.overlay .input,.nafed-page>.overlay .select,.nafed-page>.overlay .reg-field input,.nafed-page>.overlay .reg-field select{min-width:0;width:100%;height:44px;padding:10px 12px;border:1px solid #d3dce8;border-radius:5px;background:white;font:inherit;font-size:14px;color:#294365;box-shadow:none}
.nafed-page>.overlay .input:focus,.nafed-page>.overlay .select:focus,.nafed-page>.overlay .reg-field input:focus,.nafed-page>.overlay .reg-field select:focus{outline:3px solid #d4e4f5;outline-offset:1px;border-color:#6b99ca}
.nafed-page>.overlay .main-btn,.nafed-page>.overlay .cancel-btn,.nafed-page>.overlay .submit-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;min-height:44px;height:auto;padding:11px 16px;border-radius:5px;background:#0f2042;box-shadow:none;font-size:14px;font-weight:650;line-height:1.45}
.nafed-page>.overlay .green-btn,.nafed-page>.overlay .submit-btn{background:#249030!important;box-shadow:none!important}
.nafed-page>.overlay .outline-btn{color:#294365!important;border-color:#6d809e!important;background:white!important}
.nafed-page>.overlay .cancel-btn{background:white;border-color:#ced9e6;color:#526884}
.nafed-page>.overlay :where(button,input,select,a):focus-visible{outline:3px solid #6b99ca;outline-offset:3px}
.nafed-page>.overlay button:disabled{opacity:.55;cursor:not-allowed}
.nafed-page>.overlay .link-btn{font-size:13px;font-weight:600;color:#2f5c93}
.nafed-page>.overlay .login-row{font-size:13px;gap:12px;flex-wrap:wrap;margin-bottom:19px}
.nafed-page>.overlay .login-row label{display:inline-flex;align-items:center;gap:6px}
.nafed-page>.overlay input[type=checkbox]{accent-color:#249030;width:15px;height:15px;margin:0}
.nafed-page>.overlay .or{font-size:12px;font-weight:500;margin:16px 0;color:#8a98ab}
.nafed-page>.overlay .reg-grid{gap:18px}
.nafed-page>.overlay .reg-field label{font-size:13px;font-weight:600;letter-spacing:0;color:#526783;line-height:1.5}
.nafed-page>.overlay .role-scope-note{padding:14px;border:1px solid #d4e6d8;border-radius:6px;background:#f1f8f3}
.nafed-page>.overlay .role-scope-note b{font-size:14px;color:#297c38}
.nafed-page>.overlay .role-scope-note span{font-size:13px;color:#587363;line-height:1.6}
.nafed-page>.overlay .role-scope-note em{font-size:12px;font-weight:550;border-radius:4px;line-height:1.5;padding:5px 8px;background:#e2f1e6;color:#2f7440}
.nafed-page>.overlay .reg-note{border:1px solid #e9dab3;background:#fff9eb;color:#836b33;border-radius:6px;font-size:13px;font-weight:400;padding:15px;line-height:1.65}
@media(min-width:901px) and (max-width:1160px){.mission-portal .mp-brand-title{font-size:21px}.mission-portal .mp-brand p{font-size:12px}.mission-portal .mp-nav-items>a{padding-left:11px;padding-right:11px}.mission-portal .mp-nav-status{display:none}.mission-portal .mp-hero-inner{gap:28px}.mission-portal .mp-kpis{grid-template-columns:repeat(3,minmax(0,1fr))}.mission-portal .mp-kpi-top{min-height:0}.mission-portal .mp-grid{grid-template-columns:1fr 1fr}.mission-portal .mp-overview{grid-template-columns:1fr}.mission-portal .mp-live-india-map{height:280px}.mission-portal .mp-state-list{display:grid;grid-template-columns:1fr 1fr;gap:0 14px}.mission-portal .mp-state-list>h3{grid-column:1/-1}.mission-portal .mp-state-detail{grid-row:2/7;grid-column:2;margin-top:0;align-self:start}.mission-portal .mp-stage-row{grid-template-columns:repeat(5,minmax(0,1fr));gap:22px 16px}.mission-portal .mp-stage-label{min-height:37px}.mission-portal .mp-lower{grid-template-columns:1fr 1fr}.mission-portal .mp-videos{grid-template-columns:1fr}.mission-portal .mp-video-card{display:grid;grid-template-columns:130px 1fr;gap:14px}.mission-portal .mp-video-card h3{margin-top:3px}}
@media(max-width:900px){.mission-portal .mp-wrap{padding-left:24px;padding-right:24px}.mission-portal .mp-header-inner{min-height:88px;gap:18px}.mission-portal .mp-brand{gap:13px}.mission-portal .mp-logo{width:68px;height:55px;padding-right:12px}.mission-portal .mp-brand-title{font-size:19px}.mission-portal .mp-brand p{font-size:12px}.mission-portal .mp-header-actions .mp-btn{min-height:39px;font-size:13px;padding:9px 12px}.mission-portal .mp-header-actions .mp-btn svg{display:none}.mission-portal .mp-nav-inner{flex-wrap:wrap;gap:0}.mission-portal .mp-nav-toggle{display:inline-flex}.mission-portal .mp-nav-status{display:flex}.mission-portal .mp-nav-items{display:none;flex-direction:column;width:100%;padding:6px 0 12px;border-top:1px solid #ffffff1f}.mission-portal .mp-nav.is-open .mp-nav-items{display:flex}.mission-portal .mp-nav-items>a{justify-content:flex-start;min-height:43px;padding:10px 12px;border-bottom:0;border-left:3px solid transparent}.mission-portal .mp-nav-items>a[aria-current=page]{border-left-color:#66bb70}.mission-portal .mp-hero-inner{gap:24px;padding-top:31px;padding-bottom:31px}.mission-portal .mp-hero h1{font-size:36px}.mission-portal .mp-hero-description{font-size:14px}.mission-portal .mp-scheme{font-size:11px}.mission-portal .mp-hero-actions{margin-top:20px;gap:9px}.mission-portal .mp-hero-actions .mp-btn{font-size:13px;padding:9px 12px}.mission-portal .mp-hero-caption{font-size:11px;padding:10px}.mission-portal .mp-trust{gap:10px;font-size:11px}.mission-portal .mp-kpis{grid-template-columns:repeat(3,minmax(0,1fr))}.mission-portal .mp-kpi-top{min-height:0}.mission-portal .mp-grid{grid-template-columns:1fr}.mission-portal .mp-overview{grid-template-columns:1fr 1fr}.mission-portal .mp-live-india-map{height:330px}.mission-portal .mp-stage-row{grid-template-columns:repeat(3,minmax(0,1fr));gap:22px 18px}.mission-portal .mp-stage-label{min-height:0}.mission-portal .mp-stage:nth-child(3n){border:0}.mission-portal .mp-lower{grid-template-columns:1fr}.mission-portal .mp-ticker{gap:12px}.mission-portal .mp-ticker-label{font-size:11px}.mission-portal .mp-ticker-controls>.mp-text-link{display:none}.mission-portal .mp-upcoming-tools{align-items:flex-start;flex-direction:column;gap:12px}.mission-portal .mp-search{max-width:none}.mission-portal .mp-footer-inner{align-items:flex-start}.mission-portal .mp-footer-links{gap:10px 17px}}
@media(max-width:600px){.mission-portal .mp-wrap{padding-left:18px;padding-right:18px}.mission-portal .mp-top-inner{min-height:31px;font-size:11px;gap:8px}.mission-portal .mp-top-right>a{display:none}.mission-portal .mp-top-right{gap:8px}.mission-portal .mp-header-inner{min-height:0;flex-wrap:wrap;padding-top:15px;padding-bottom:15px;gap:15px}.mission-portal .mp-brand{width:100%;gap:13px}.mission-portal .mp-logo{width:65px;height:55px}.mission-portal .mp-brand-title{font-size:19px;line-height:1.3}.mission-portal .mp-brand p{font-size:12px;max-width:280px;line-height:1.5}.mission-portal .mp-header-actions{width:100%;gap:10px}.mission-portal .mp-header-actions .mp-btn{flex:1;font-size:13px;min-height:41px}.mission-portal .mp-header-actions .mp-btn svg{display:block}.mission-portal .mp-hero-inner{grid-template-columns:1fr;gap:25px;padding-top:31px;padding-bottom:28px}.mission-portal .mp-scheme{margin-bottom:13px;letter-spacing:.04em;font-size:11px}.mission-portal .mp-hero h1{font-size:clamp(34px,9vw,44px);margin-bottom:15px}.mission-portal .mp-hero-description{font-size:15px;line-height:1.7}.mission-portal .mp-hero-actions .mp-btn{min-height:42px;font-size:13px;flex:1}.mission-portal .mp-trust{margin-top:20px;padding-top:16px;gap:10px 16px;font-size:12px}.mission-portal .mp-hero-caption{font-size:12px}.mission-portal .mp-hero-image{aspect-ratio:1.7}.mission-portal .mp-content{padding-top:24px;padding-bottom:24px}.mission-portal .mp-section-heading{gap:12px}.mission-portal .mp-section-heading h2{font-size:23px}.mission-portal .mp-section-heading p{font-size:13px}.mission-portal .mp-section-heading>.mp-connection{display:none}.mission-portal .mp-kpis{grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;margin-bottom:20px}.mission-portal .mp-kpi{padding:15px 13px}.mission-portal .mp-kpi-top{font-size:12px;min-height:35px}.mission-portal .mp-kpi-value{font-size:26px}.mission-portal .mp-kpi small{font-size:11px}.mission-portal .mp-ticker{gap:9px;padding:9px 10px;margin-bottom:20px}.mission-portal .mp-ticker-label>span{display:none}.mission-portal .mp-ticker-item{font-size:12px;gap:7px}.mission-portal .mp-ticker-item b{font-size:11px}.mission-portal .mp-panel-head{padding:16px;gap:12px;min-height:0}.mission-portal .mp-panel-head h2,.mission-portal .mp-panel-head h3{font-size:16px}.mission-portal .mp-panel-head p{font-size:12px}.mission-portal .mp-panel-head>.mp-text-link{font-size:12px;white-space:normal;text-align:right;justify-content:flex-end}.mission-portal .mp-overview{grid-template-columns:1fr;gap:18px;padding:16px}.mission-portal .mp-live-india-map{height:340px}.mission-portal .mp-state-list{display:block}.mission-portal .mp-state-list>h3{font-size:13px}.mission-portal .mp-state-row{font-size:13px;padding:11px 3px}.mission-portal .mp-state-row b{font-size:13px}.mission-portal .mp-state-detail{padding:14px}.mission-portal .mp-state-detail h3{font-size:15px}.mission-portal .mp-state-metric,.mission-portal .mp-state-metric b{font-size:13px}.mission-portal .mp-activity{padding:0 16px}.mission-portal .mp-activity-top{align-items:flex-start;gap:8px;flex-wrap:wrap}.mission-portal .mp-activity-top time{font-size:11px}.mission-portal .mp-activity-row{gap:9px}.mission-portal .mp-auto{padding:12px 16px;font-size:11px}.mission-portal .mp-pipeline{padding:17px 16px}.mission-portal .mp-stage-row{grid-template-columns:repeat(3,minmax(0,1fr));gap:20px 12px}.mission-portal .mp-stage-label{font-size:11px;min-height:33px}.mission-portal .mp-stage b{font-size:23px}.mission-portal .mp-stage-number{width:24px;height:24px;font-size:10px}.mission-portal .mp-pipeline-total{gap:8px;font-size:12px;align-items:flex-start;flex-direction:column}.mission-portal .mp-pipeline-total b{margin-left:5px}.mission-portal .mp-opportunity-tabs{padding:0 16px;gap:20px}.mission-portal .mp-tab{font-size:13px;gap:6px}.mission-portal .mp-tab-count{font-size:11px;min-width:21px}.mission-portal .mp-upcoming-tools{padding:15px 16px}.mission-portal .mp-search{min-width:0}.mission-portal .mp-table-footer{padding:13px 16px}.mission-portal .mp-pagination{width:100%;justify-content:space-between}.mission-portal .mp-videos{grid-template-columns:1fr;padding:16px;gap:17px}.mission-portal .mp-video-card{display:grid;grid-template-columns:120px minmax(0,1fr);gap:13px}.mission-portal .mp-video-card h3{font-size:13px;margin-top:1px}.mission-portal .mp-video-card p{font-size:12px}.mission-portal .mp-quick-grid{padding:3px 9px}.mission-portal .mp-quick{padding:12px 8px;gap:8px;font-size:12px;min-height:65px}.mission-portal .mp-quick>svg{width:18px;height:18px}.mission-portal .mp-footer-inner{flex-direction:column;gap:15px}.mission-portal .mp-footer-links{gap:10px 20px}.nafed-page>.overlay{padding:12px}.nafed-page>.overlay .modal{max-height:calc(100dvh - 24px)}.nafed-page>.overlay .modal-head{padding:20px}.nafed-page>.overlay .modal-head h2{font-size:21px}.nafed-page>.overlay .modal-body{padding:20px}.nafed-page>.overlay .login-role-grid{grid-template-columns:1fr}.nafed-page>.overlay .login-role-option{min-height:65px}.nafed-page>.overlay .reg-grid{grid-template-columns:1fr;gap:16px}.nafed-page>.overlay .reg-field.full{grid-column:auto}.nafed-page>.overlay .reg-actions{flex-direction:column-reverse}.nafed-page>.overlay .reg-actions button{width:100%;flex:auto}.nafed-page>.overlay .reg-note{font-size:13px}}
@media(prefers-reduced-motion:reduce){.mission-portal *,.mission-portal *::before,.mission-portal *::after{animation:none!important;transition:none!important;scroll-behavior:auto!important}.mission-portal .mp-ticker-runner{transform:none!important}}
`;
function MissionPortalHome({
  summary,
  states,
  activities,
  tenders,
  highlights,
  connection,
  formatCapacity,
  formatMoney,
  formatDate,
  videoEmbed,
  onLogin,
  onRegister,
  upcomingPacs = [],
  upcomingState = "loading",
  tenderState = "loading"
}) {
  const registeredPacs = publicCount(summary.pacsRegistered, summary.registeredPacs, summary.pacsTotal, summary.pacs);
  const appliedPacs = publicCount(summary.applicationsSubmitted, summary.pacsApplied, summary.appliedPacs, summary.pacsWithApplications, summary.projects);
  const approvedPacs = publicCount(summary.pacsApproved, summary.approvedPacs, summary.approvedVisiblePacs, summary.pacs);
  const totalProjects = publicCount(summary.projectsTotal, summary.totalProjects, summary.projects);
  const implementationProjects = publicCount(summary.workStartedProjects, summary.implementationProjects, summary.activeProjects);
  const totalCapacityMt = parseMetricNumber(summary.totalCapacityMt ?? summary.capacityMt);
  const displayStates = states;
  const pipeline = [
    ["PACS Registered", registeredPacs],
    ["PACS Approved", approvedPacs],
    ["PMC Allocated", publicCount(summary.pacsAllocated, summary.activeAllocations)],
    ["Application Submitted", appliedPacs],
    ["DCDC Approved", publicCount(summary.dcdcApproved)],
    ["MOU Executed", publicCount(summary.mouExecuted, summary.executedMous, summary.mou)],
    ["DPR Approved", publicCount(summary.dprApproved, summary.dpr)],
    ["Projects Tendered", publicCount(summary.tenderPublished, summary.tender)],
    ["Project Completed", publicCount(summary.completedProjects, summary.completed)]
  ];
  const topStates = [...displayStates].filter((item) => Number(item.pacs || item.projects || item.capacityMt || 0)).sort((a, b) => Number(b.capacityMt || 0) - Number(a.capacityMt || 0)).slice(0, 5);
  const activityRows = activities.slice(0, 6);
  const tenderRows = tenders.slice(0, 5);
  const videoRows = highlights.slice(0, 3);
  const [selectedState, setSelectedState] = useState2(null);
  const [menuOpen, setMenuOpen] = useState2(false);
  const [tickerPaused, setTickerPaused] = useState2(false);
  const [noticeTab, setNoticeTab] = useState2("published");
  const [upcomingSearch, setUpcomingSearch] = useState2("");
  const [upcomingPage, setUpcomingPage] = useState2(1);
  const selectedStateData = selectedState && displayStates.find((item) => mapKey(item.state || item.name) === mapKey(selectedState.state || selectedState.name)) || topStates[0] || displayStates[0] || { state: "India", pacs: 0, projects: 0, capacityMt: 0 };
  const isLive = connection === "LIVE";
  const connectionLabel = isLive ? "Live public data" : connection === "CONNECTING" ? "Connecting to public data" : "Public data unavailable";
  const upcomingQuery = upcomingSearch.trim().toLocaleLowerCase();
  const filteredUpcoming = useMemo2(() => upcomingPacs.filter(
    (item) => !upcomingQuery || [item.name, item.state, item.district, item.stage].some((value) => String(value || "").toLocaleLowerCase().includes(upcomingQuery))
  ), [upcomingPacs, upcomingQuery]);
  const upcomingPageSize = 10;
  const upcomingPages = Math.max(1, Math.ceil(filteredUpcoming.length / upcomingPageSize));
  const currentUpcomingPage = Math.min(upcomingPage, upcomingPages);
  const upcomingStart = (currentUpcomingPage - 1) * upcomingPageSize;
  const upcomingRows = filteredUpcoming.slice(upcomingStart, upcomingStart + upcomingPageSize);
  const upcomingLoading = upcomingState === "loading";
  const upcomingUnavailable = ["error", "unavailable"].includes(upcomingState);
  const handleNoticeTabKeyDown = (event) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const nextTab = event.key === "Home" ? "published" : event.key === "End" ? "upcoming" : noticeTab === "published" ? "upcoming" : "published";
    setNoticeTab(nextTab);
    event.currentTarget.querySelector(`#mp-${nextTab}-tab`)?.focus();
  };
  const metrics = [
    ["States / UTs", summary.states, "map", "Programme coverage"],
    ["State Branches", summary.branches, "branch", "Published branch records"],
    ["Registered PACS", registeredPacs, "users", `${approvedPacs.toLocaleString("en-IN")} approved PACS`],
    ["Godown Applications", totalProjects, "file", `${implementationProjects.toLocaleString("en-IN")} implementation started`],
    ["Storage Capacity", formatCapacity(totalCapacityMt), "storage", "Published application capacity"],
    ["Published Tenders", tenderState === "live" ? tenders.length : "—", "file", tenderState === "error" ? "Notice feed unavailable" : "Public procurement notices"]
  ];
  const services = [
    ["storage", "Godown Registration", "#register"],
    ["check", "Check Status", "/nafed/help"],
    ["location", "Track Application", "/nafed/help"],
    ["file", "Guidelines & Manuals", "/nafed/guidelines"],
    ["building", "Tender Notices", "#tenders"],
    ["phone", "Contact NAFED", "/nafed/contact"],
    ["chart", "Capacity Calculator", "/nafed/help"],
    ["help", "Help & Support", "/nafed/help"]
  ];
  return <div className="mission-portal">
      <style>{MISSION_PORTAL_STYLES}</style>
      <a className="mp-skip" href="#command-centre">Skip to main content</a>
      <div className="mp-top">
        <div className="mp-wrap mp-top-inner">
          <span>PM Anna Bhandaran Yojana</span>
          <div className="mp-top-right">
            <a href="/nafed/help">Help & support</a>
            <span className={`mp-connection ${isLive ? "is-live" : ""}`} role="status"><i className="mp-live-dot" />{isLive ? "Live public data" : "Public portal"}</span>
          </div>
        </div>
      </div>
      <header className="mp-header">
        <div className="mp-wrap mp-header-inner">
          <a className="mp-brand" href="/nafed" aria-label="NAFED Grain Storage Digital Mission home">
            <img className="mp-logo" src={NAFED_LOGO} alt="NAFED" />
            <div>
              <div className="mp-brand-title"><strong>NAFED</strong> Grain Storage Digital Mission</div>
              <p>Integrated programme monitoring & approval platform</p>
            </div>
          </a>
          <div className="mp-header-actions">
            <button type="button" className="mp-btn primary" onClick={onLogin}><PortalIcon name="lock" size={17} />Portal Login</button>
            <button type="button" className="mp-btn" onClick={onRegister}><PortalIcon name="user" size={17} />Registration</button>
          </div>
        </div>
      </header>
      <nav className={`mp-nav ${menuOpen ? "is-open" : ""}`} aria-label="Public portal navigation">
        <div className="mp-wrap mp-nav-inner">
          <button type="button" className="mp-nav-toggle" aria-expanded={menuOpen} aria-controls="mp-public-menu" onClick={() => setMenuOpen((open) => !open)}><PortalIcon name={menuOpen ? "close" : "menu"} />Menu</button>
          <div className="mp-nav-items" id="mp-public-menu" onClick={() => setMenuOpen(false)}>
            <a href="/nafed" aria-current="page">Home</a>
            <a href="/nafed/about">About the Mission</a>
            <a href="/nafed/guidelines">Guidelines</a>
            <a href="/tender/list?channel=NAFED">Tenders</a>
            <a href="#godown-registration" onClick={(event) => {
    event.preventDefault();
    onRegister();
  }}>Godown Registration</a>
            <a href="/nafed/help">Help Desk</a>
            <a href="/nafed/contact">Contact</a>
          </div>
          <span className="mp-nav-status"><PortalIcon name="shield" size={15} />Role-based portal access</span>
        </div>
      </nav>

      <section className="mp-hero" aria-labelledby="mp-hero-title">
        <div className="mp-wrap mp-hero-inner">
          <div className="mp-hero-copy">
            <div className="mp-scheme">Pradhan Mantri Anna Bhandaran Yojana</div>
            <h1 id="mp-hero-title">One Nation.<span>One Storage Network.</span></h1>
            <p className="mp-hero-description">Connecting PACS, state branches, banks and project agencies through one digital platform—from registration and approval to modern grain storage infrastructure.</p>
            <div className="mp-hero-actions">
              <button type="button" className="mp-btn primary" onClick={onLogin}>Access Your Portal<PortalIcon name="arrow" size={17} /></button>
              <a className="mp-btn" href="/nafed/help">Track Your Application</a>
            </div>
            <div className="mp-trust">
              <span><PortalIcon name="shield" size={15} />Role-based access</span>
              <span><PortalIcon name="check" size={15} />Transparent approvals</span>
              <span><PortalIcon name="chart" size={15} />Project monitoring</span>
            </div>
          </div>
          <figure className="mp-hero-visual">
            <img className="mp-hero-image" src={HERO_IMAGE} alt="Cooperative grain storage infrastructure" />
            <figcaption className="mp-hero-caption"><PortalIcon name="storage" size={17} />A connected approach to cooperative grain storage</figcaption>
          </figure>
        </div>
      </section>

      <main className="mp-wrap mp-content" id="command-centre" tabIndex={-1}>
        <div className="mp-section-heading">
          <div><div className="mp-eyebrow">Programme at a glance</div><h2>National programme overview</h2><p>Published figures from the public programme dashboard.</p></div>
          <span className={`mp-connection ${isLive ? "is-live" : ""}`}><i className="mp-live-dot" />{connectionLabel}</span>
        </div>
        <section className="mp-kpis" aria-label="Published programme statistics">
          {metrics.map(([label, value, icon, note]) => <div className="mp-kpi" key={label}>
            <div className="mp-kpi-top"><PortalIcon name={icon} size={18} /><span>{label}</span></div>
            <strong className="mp-kpi-value">{typeof value === "number" ? value.toLocaleString("en-IN") : value ?? "—"}</strong>
            <small>{note}</small>
          </div>)}
        </section>
        <section className={`mp-ticker ${tickerPaused ? "is-paused" : ""}`} aria-label="Latest public activity">
          <div className="mp-ticker-label"><PortalIcon name="clock" size={17} /><span>Latest updates</span></div>
          <div className="mp-ticker-track"><div className="mp-ticker-runner">
            {activities.length ? activities.slice(0, 3).map((item) => <span className="mp-ticker-item" key={item.id}><b>{formatDate(item.createdAt)}</b>{item.state} · {item.title}</span>) : <span className="mp-ticker-item">Published programme updates will appear here.</span>}
          </div></div>
          <div className="mp-ticker-controls">
            <button type="button" className="mp-ticker-pause" aria-label={tickerPaused ? "Resume activity ticker" : "Pause activity ticker"} aria-pressed={tickerPaused} onClick={() => setTickerPaused((paused) => !paused)}><PortalIcon name={tickerPaused ? "play" : "pause"} size={14} /></button>
            <a className="mp-text-link" href="#activity">View activity<PortalIcon name="arrow" size={15} /></a>
          </div>
        </section>

        <section className="mp-grid" aria-label="State coverage and programme updates">
          <article className="mp-panel" id="states">
            <div className="mp-panel-head"><div><h2>Programme footprint</h2><p>Select a state or union territory to explore its published figures.</p></div><PortalIcon name="map" size={21} /></div>
            <div className="mp-overview">
              <div className="mp-map"><IndiaLiveMap states={displayStates} selectedState={selectedStateData} onSelect={setSelectedState} /><small>Click a state, or select one from the list.</small></div>
              <div className="mp-state-list">
                <h3>Top states by storage capacity</h3>
                {(topStates.length ? topStates : displayStates.slice(0, 5)).map((state, index) => <button type="button" className="mp-state-row" key={state.state} aria-pressed={mapKey(state.state) === mapKey(selectedStateData.state)} onClick={() => setSelectedState(state)}><span>{String(index + 1).padStart(2, "0")}</span><span>{state.state}</span><b>{formatCapacity(state.capacityMt)}</b></button>)}
                <div className="mp-state-detail" aria-live="polite">
                  <h3>{selectedStateData.state || "India"}</h3>
                  <div className="mp-state-metrics">
                    <div className="mp-state-metric"><span>Registered PACS</span><b>{Number(selectedStateData.pacs || 0).toLocaleString("en-IN")}</b></div>
                    <div className="mp-state-metric"><span>Godown applications</span><b>{Number(selectedStateData.projects || 0).toLocaleString("en-IN")}</b></div>
                    <div className="mp-state-metric"><span>Storage capacity</span><b>{formatCapacity(selectedStateData.capacityMt)}</b></div>
                  </div>
                </div>
              </div>
            </div>
          </article>
          <article className="mp-panel" id="activity">
            <div className="mp-panel-head"><div><h2>Approval & workflow updates</h2><p>Latest published programme activity.</p></div><a className="mp-text-link" href="/nafed/help">Need help?<PortalIcon name="arrow" size={14} /></a></div>
            <div className="mp-activity">
              {activityRows.length ? activityRows.map((item) => <div className="mp-activity-row" key={item.id}>
                <div className="mp-activity-icon"><PortalIcon name="check" size={15} /></div>
                <div><div className="mp-activity-top"><span className="mp-type" title={item.type}>{item.type}</span><time>{formatDate(item.createdAt)}</time></div><p className="mp-activity-text">{item.title}</p><div className="mp-activity-meta"><span>{item.state}</span><span>·</span><span>{item.actor}</span></div></div>
              </div>) : <div className="mp-empty"><PortalIcon name="clock" size={26} /><strong>No public activity yet</strong>Published programme updates will appear here automatically.</div>}
            </div>
            <div className={`mp-auto mp-connection ${isLive ? "is-live" : ""}`}><i className="mp-live-dot" />{connectionLabel}</div>
          </article>
        </section>

        <section className="mp-panel mp-pipeline-panel" id="pipeline" aria-labelledby="mp-pipeline-title">
          <div className="mp-panel-head"><div><h2 id="mp-pipeline-title">Approval & project pipeline</h2><p>A clear view of each published stage in the programme.</p></div><PortalIcon name="branch" size={21} /></div>
          <div className="mp-pipeline">
            <div className="mp-stage-row">{pipeline.map(([label, value], index) => {
    const current = publicCount(value);
    const previous = index === 0 ? current : publicCount(pipeline[index - 1][1]);
    const width = index === 0 ? current ? 100 : 0 : previous ? Math.min(100, current / previous * 100) : 0;
    return <div className="mp-stage" key={label}><span className="mp-stage-number">{String(index + 1).padStart(2, "0")}</span><span className="mp-stage-label">{label}</span><b>{current.toLocaleString("en-IN")}</b><div className="mp-progress" aria-hidden="true"><i style={{ width: `${width}%` }} /></div></div>;
  })}</div>
            <div className="mp-pipeline-total"><span>Applications submitted after PMC allocation<b>{totalProjects.toLocaleString("en-IN")}</b></span><span>Published application capacity<b>{formatCapacity(totalCapacityMt)}</b></span></div>
          </div>
        </section>

        <section className="mp-panel mp-opportunities" id="tenders" aria-labelledby="mp-opportunities-title">
          <div className="mp-panel-head"><div><h2 id="mp-opportunities-title">Tenders & upcoming PACS</h2><p>Published procurement notices and projects awaiting a tender notice.</p></div><a className="mp-text-link" href="/tender/list?channel=NAFED">View all tenders<PortalIcon name="arrow" size={15} /></a></div>
          <div className="mp-opportunity-tabs" role="tablist" aria-label="Tender notice views" onKeyDown={handleNoticeTabKeyDown}>
            <button type="button" id="mp-published-tab" className="mp-tab" role="tab" tabIndex={noticeTab === "published" ? 0 : -1} aria-selected={noticeTab === "published"} aria-controls="mp-published-notices" onClick={() => setNoticeTab("published")}>Published notices<span className="mp-tab-count">{tenders.length.toLocaleString("en-IN")}</span></button>
            <button type="button" id="mp-upcoming-tab" className="mp-tab" role="tab" tabIndex={noticeTab === "upcoming" ? 0 : -1} aria-selected={noticeTab === "upcoming"} aria-controls="mp-upcoming-notices" onClick={() => setNoticeTab("upcoming")}>Upcoming PACS<span className="mp-tab-count">{upcomingPacs.length.toLocaleString("en-IN")}</span></button>
          </div>
          <div id="mp-published-notices" role="tabpanel" aria-labelledby="mp-published-tab" hidden={noticeTab !== "published"}>
            <div className="mp-table-scroll" tabIndex={0} aria-label="Published tender notices; scroll horizontally for all columns">
              <table className="mp-tender-table"><thead><tr><th scope="col">Tender No.</th><th scope="col">PACS / Project</th><th scope="col">State</th><th scope="col">Capacity</th><th scope="col">Value</th><th scope="col">Closing</th><th scope="col">Status</th></tr></thead><tbody>
                {tenderRows.length ? tenderRows.map((tender) => <tr key={tender.id}><td className="mp-notice-id">{tender.tenderNo}</td><td className="mp-tender-name">{tender.detailsHref ? <a className="mp-text-link" href={tender.detailsHref}>{tender.societyName || tender.pacsName || tender.title}<PortalIcon name="arrow" size={14} /></a> : <strong>{tender.societyName || tender.pacsName || tender.title}</strong>}{(tender.societyName || tender.pacsName) && <div className="mp-notice-pending">{tender.title}</div>}</td><td>{tender.state}</td><td>{Number(tender.capacity) > 0 ? formatCapacity(tender.capacity) : "—"}</td><td>{formatMoney(tender.value)}</td><td>{formatDate(tender.closingDate)}</td><td><span className="mp-live-status">{tender.status}</span></td></tr>) : <tr><td colSpan={7}><div className="mp-empty"><PortalIcon name="file" size={27} /><strong>{tenderState === "error" ? "Tender notice feed unavailable" : tenderState === "loading" ? "Loading published notices…" : "No published notices to display"}</strong>{tenderState === "error" ? "Please try again later." : "Public tender notices will appear after publication."}</div></td></tr>}
              </tbody></table>
            </div>
            <div className="mp-table-footer"><span>Latest {tenderRows.length} published notices · Status is shown as published</span><a className="mp-text-link" href="/tender/list?channel=NAFED">Open tender notice board<PortalIcon name="arrow" size={15} /></a></div>
          </div>
          <div id="mp-upcoming-notices" role="tabpanel" aria-labelledby="mp-upcoming-tab" hidden={noticeTab !== "upcoming"}>
            <div className="mp-upcoming-tools"><p>These PACS are in the upcoming project pipeline. A tender number, bid schedule and application link will appear only after an actual notice is published.</p><label className="mp-search"><PortalIcon name="search" size={17} /><input type="search" value={upcomingSearch} aria-label="Search upcoming PACS by name, state, district or stage" placeholder="Search PACS or location" onChange={(event) => {
    setUpcomingSearch(event.target.value);
    setUpcomingPage(1);
  }} /></label></div>
            {upcomingLoading && !upcomingPacs.length ? <div className="mp-empty" role="status">Loading upcoming PACS…</div> : upcomingUnavailable && !upcomingPacs.length ? <div className="mp-empty" role="status">Upcoming project information is temporarily unavailable. Please try again later.</div> : <>
              <div className="mp-table-scroll" tabIndex={0} aria-label="Upcoming PACS; scroll horizontally for all columns"><table className="mp-tender-table mp-upcoming-table"><thead><tr><th scope="col">PACS / Society</th><th scope="col">State / District</th><th scope="col">Capacity</th><th scope="col">Project Stage</th><th scope="col">Notice Status</th></tr></thead><tbody>
                {upcomingRows.length ? upcomingRows.map((item) => <tr key={item.id || item.sourceId}><td className="mp-tender-name">{item.name || "PACS / Society"}</td><td>{item.state || "—"}{item.district && <div className="mp-notice-pending">{item.district}</div>}</td><td>{item.capacityMt ? formatCapacity(item.capacityMt) : "—"}</td><td>{item.stage || "—"}</td><td><span className="mp-upcoming-badge">Upcoming</span><span className="mp-notice-pending">Notice pending</span></td></tr>) : <tr><td colSpan={5}><div className="mp-empty"><strong>{upcomingSearch ? "No PACS match your search" : "No upcoming PACS published yet"}</strong>{upcomingSearch ? "Try another name, state or district." : "Upcoming projects will appear here when available."}</div></td></tr>}
              </tbody></table></div>
              <div className="mp-table-footer"><span aria-live="polite">{filteredUpcoming.length ? `${upcomingStart + 1}–${Math.min(upcomingStart + upcomingPageSize, filteredUpcoming.length)} of ${filteredUpcoming.length.toLocaleString("en-IN")} PACS` : "0 PACS"}{upcomingSearch ? " matching your search" : ""}</span><div className="mp-pagination" aria-label="Upcoming PACS pagination"><button type="button" disabled={currentUpcomingPage <= 1} onClick={() => setUpcomingPage(currentUpcomingPage - 1)}><PortalIcon name="chevron" size={13} />Previous</button><span>Page {currentUpcomingPage} of {upcomingPages}</span><button type="button" disabled={currentUpcomingPage >= upcomingPages} onClick={() => setUpcomingPage(currentUpcomingPage + 1)}>Next<PortalIcon name="chevron" size={13} /></button></div></div>
            </>}
          </div>
        </section>

        <section className="mp-lower" aria-label="Project highlights and citizen services">
          <article className="mp-panel" id="videos">
            <div className="mp-panel-head"><div><h2>Project video highlights</h2><p>Published field progress and programme stories.</p></div><PortalIcon name="play" size={21} /></div>
            <div className="mp-videos">
              {videoRows.length ? videoRows.map((video) => {
    const embed = videoEmbed(video.videoUrl);
    return <article className="mp-video-card" key={video.id}><div className="mp-video-media">{video.thumbnailUrl ? <img src={video.thumbnailUrl} alt="" loading="lazy" /> : <PortalIcon name="storage" size={36} />}<a className="mp-play" href={video.videoUrl || embed} target="_blank" rel="noreferrer" aria-label={`Watch ${video.title || "project progress highlight"} (opens in new tab)`}><span><PortalIcon name="play" size={19} /></span></a></div><div><h3>{video.title || "Project Progress Highlight"}</h3><p>{video.state || "National"} · Published highlight</p></div></article>;
  }) : <div className="mp-empty" style={{ gridColumn: "1/-1" }}><PortalIcon name="play" size={26} /><strong>Project stories are on their way</strong>Published video highlights will appear here.</div>}
            </div>
          </article>
          <article className="mp-panel" id="godown-registration">
            <div className="mp-panel-head"><div><h2>Citizen & programme services</h2><p>Information, registration and support in one place.</p></div><PortalIcon name="help" size={21} /></div>
            <div className="mp-quick-grid">{services.map(([icon, label, url]) => <a className="mp-quick" href={url} key={label} onClick={label === "Godown Registration" ? (event) => {
    event.preventDefault();
    onRegister();
  } : void 0}><PortalIcon name={icon} size={20} /><span>{label}</span></a>)}</div>
          </article>
        </section>
      </main>
      <footer className="mp-footer"><div className="mp-wrap mp-footer-inner"><div><strong>NAFED Grain Storage Digital Mission</strong><p>PM Anna Bhandaran Yojana · Powered by Shine Agri Construction</p></div><div className="mp-footer-links"><a href="/nafed/guidelines">Guidelines</a><a href="/nafed/help">Help Desk</a><a href="/nafed/contact">Contact</a></div></div></footer>
    </div>;
}
function NafedHomePage() {
  const [activeSlide, setActiveSlide] = useState2(0);
  const [email, setEmail] = useState2("");
  const [password, setPassword] = useState2("");
  const [loading, setLoading] = useState2(false);
  const [selectedRole, setSelectedRole] = useState2("PMC");
  const [showLoginPopup, setShowLoginPopup] = useState2(false);
  const [showRegisterPopup, setShowRegisterPopup] = useState2(false);
  const [loginMode, setLoginMode] = useState2("PASSWORD");
  const [role, setRole] = useState2("PACS");
  const [name, setName] = useState2("");
  const [organizationName, setOrganizationName] = useState2("");
  const [registrationNumber, setRegistrationNumber] = useState2("");
  const [designation, setDesignation] = useState2("");
  const [mobile, setMobile] = useState2("");
  const [confirmPassword, setConfirmPassword] = useState2("");
  const [regState, setRegState] = useState2("Gujarat");
  const [regDistrict, setRegDistrict] = useState2("");
  const [regTaluka, setRegTaluka] = useState2("");
  const [regVillage, setRegVillage] = useState2("");
  const [regAddress, setRegAddress] = useState2("");
  const [districtOptions, setDistrictOptions] = useState2(GUJARAT_DISTRICTS);
  const [districtLoading, setDistrictLoading] = useState2(false);
  const [otp, setOtp] = useState2("");
  const [confirmation, setConfirmation] = useState2(null);
  const [stateStats, setStateStats] = useState2(DEFAULT_STATE_STATS);
  const [branchCount, setBranchCount] = useState2(0);
  const [publicSummary, setPublicSummary] = useState2(null);
  const [liveActivities, setLiveActivities] = useState2([]);
  const [liveTenders, setLiveTenders] = useState2([]);
  const [tenderFeedState, setTenderFeedState] = useState2("loading");
  const { upcomingPacs, upcomingState } = useNafedUpcomingPacs(liveTenders);
  const [videoHighlights, setVideoHighlights] = useState2([]);
  const [liveConnection, setLiveConnection] = useState2("CONNECTING");
  const registrationConfig = ROLE_REGISTRATION_CONFIG[role] || ROLE_REGISTRATION_CONFIG.PACS;
  const approvalConfig = ROLE_APPROVAL_CONFIG[role] || ROLE_APPROVAL_CONFIG.PACS;
  const identityConfig = ROLE_IDENTITY_CONFIG[role] || ROLE_IDENTITY_CONFIG.PACS;
  useEffect2(() => {
    setOrganizationName("");
    setRegistrationNumber("");
    setDesignation("");
    if (!registrationConfig.district) setRegDistrict("");
    if (!registrationConfig.taluka) setRegTaluka("");
    if (!registrationConfig.village) setRegVillage("");
    if (!registrationConfig.address) setRegAddress("");
  }, [
    role,
    registrationConfig.district,
    registrationConfig.taluka,
    registrationConfig.village,
    registrationConfig.address
  ]);
  useEffect2(() => {
    if (HERO_SLIDES.length < 2) return void 0;
    const timer = window.setInterval(
      () => setActiveSlide((current) => (current + 1) % HERO_SLIDES.length),
      6500
    );
    return () => window.clearInterval(timer);
  }, []);
  useEffect2(() => {
    const unsubscribers = [];
    unsubscribers.push(
      onSnapshot2(
        doc(db2, "nafed_public_dashboard", "summary"),
        (snapshot) => {
          const summary = snapshot.exists() ? snapshot.data() : null;
          setPublicSummary(summary);
          setBranchCount(Number(summary?.branches || 0));
          setLiveConnection("LIVE");
        },
        (error) => console.warn("Public summary unavailable:", error.code)
      )
    );
    unsubscribers.push(
      onSnapshot2(
        collection2(db2, "nafed_public_state_stats"),
        (snapshot) => {
          if (snapshot.empty) return;
          const incoming = snapshot.docs.map((item) => ({
            state: item.data().state || item.id,
            pacs: publicCount(
              item.data().pacsRegistered,
              item.data().registeredPacs,
              item.data().pacs,
              item.data().pacsApproved,
              item.data().approvedPacs
            ),
            projects: publicCount(
              item.data().projectsTotal,
              item.data().totalProjects,
              item.data().projects
            ),
            capacityMt: capacityFrom(item.data())
          }));
          setStateStats(
            DEFAULT_STATE_STATS.map(
              (base) => incoming.find(
                (row) => row.state.toLowerCase() === base.state.toLowerCase()
              ) || base
            )
          );
          setLiveConnection("LIVE");
        },
        (error) => console.warn("Public State MIS unavailable:", error.code)
      )
    );
    unsubscribers.push(
      onSnapshot2(
        query(
          collection2(db2, "nafed_public_highlights"),
          orderBy("publishedAt", "desc"),
          limit(6)
        ),
        (snapshot) => {
          setVideoHighlights(
            snapshot.docs.map((item) => ({ id: item.id, ...item.data() })).filter(
              (item) => item.isPublished !== false && !item.isDeleted && !item.deletedAt && item.videoUrl
            )
          );
        },
        (error) => console.warn("Public highlights unavailable:", error.code)
      )
    );
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, []);
  useEffect2(() => {
    const unsubscribers = [];
    let activityConnected = false;
    let tenderConnected = false;
    const normaliseActivity = (item = {}, id = "") => {
      const rawType = String(
        item.type || item.action || item.event || "UPDATE"
      ).toUpperCase();
      const rawTitle = item.title || item.message || item.description || item.actionLabel || rawType.replaceAll("_", " ");
      return {
        id,
        sourceId: item.sourceId || item.projectId || item.pacsId || item.userId || item.applicationId || "",
        title: cleanActivityTitle(rawTitle),
        actor: item.actor || item.actorName || item.performedByName || item.organizationName || item.performedByRole || item.role || "NAFED Portal",
        state: item.state || item.stateName || item.projectState || "National",
        type: activityTypeLabel(rawType),
        rawType,
        status: String(item.status || item.recordStatus || "").toUpperCase(),
        isDeleted: item.isDeleted === true || item.deleted === true || Boolean(item.deletedAt || item.archivedAt || item.removedAt),
        createdAt: item.createdAt || item.updatedAt || item.timestamp || null
      };
    };
    const normaliseTender = normaliseNafedPublicTender;
    const timeValue = (value) => {
      if (!value) return 0;
      if (typeof value?.toMillis === "function") return value.toMillis();
      if (value?.seconds) return value.seconds * 1e3;
      const parsed = new Date(value).getTime();
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const subscribe = (collectionName, kind) => {
      const liveQuery = kind === "ACTIVITY" ? query(collection2(db2, collectionName), orderBy("createdAt", "desc"), limit(30)) : collection2(db2, collectionName);
      const unsubscribe = onSnapshot2(
        liveQuery,
        (snapshot) => {
          const rows = snapshot.docs.map(
            (item) => kind === "ACTIVITY" ? normaliseActivity(item.data(), item.id) : normaliseTender(item.data(), item.id)
          ).filter(Boolean).filter(
            (item) => !item.isDeleted && !REMOVED_STATUSES.has(String(item.status || "").toUpperCase())
          ).sort((a, b) => timeValue(b.createdAt) - timeValue(a.createdAt)).filter((item, index, allRows) => {
            if (kind === "TENDER") return index === allRows.findIndex((row) => row.sourceCollection === item.sourceCollection && row.canonicalId === item.canonicalId);
            const identity = [
              item.sourceId || "",
              item.rawType || item.type,
              item.state,
              item.title.toLowerCase()
            ].join("|");
            return index === allRows.findIndex(
              (row) => [
                row.sourceId || "",
                row.rawType || row.type,
                row.state,
                row.title.toLowerCase()
              ].join("|") === identity
            );
          });
          if (kind === "ACTIVITY") {
            activityConnected = true;
            setLiveActivities(rows);
          } else {
            tenderConnected = true;
            setLiveTenders(rows);
            setTenderFeedState("live");
          }
          setLiveConnection("LIVE");
        },
        (error) => {
          console.warn(`${collectionName} live feed unavailable:`, error.code);
          if (kind === "TENDER") {
            setLiveTenders([]);
            setTenderFeedState("error");
          }
          if (!activityConnected && !tenderConnected)
            setLiveConnection("PUBLIC_FEED_REQUIRED");
        }
      );
      unsubscribers.push(unsubscribe);
    };
    subscribe("nafed_public_activity", "ACTIVITY");
    subscribe("nafed_public_tenders", "TENDER");
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, []);
  useEffect2(() => {
    let active = true;
    async function loadDistricts() {
      setRegDistrict("");
      if (!registrationConfig.district) {
        setDistrictOptions([]);
        setDistrictLoading(false);
        return;
      }
      if (!regState) {
        setDistrictOptions([]);
        return;
      }
      try {
        setDistrictLoading(true);
        const snap = await getDocs(
          query(
            collection2(db2, "location_master"),
            where("state", "==", regState),
            where("type", "==", "district")
          )
        );
        const districts = snap.docs.map((item) => String(item.data()?.name || "").trim()).filter(Boolean).sort((a, b) => a.localeCompare(b));
        if (!active) return;
        setDistrictOptions(
          districts.length ? districts : regState === "Gujarat" ? GUJARAT_DISTRICTS : []
        );
      } catch (error) {
        console.error("District loading failed:", error);
        if (active) {
          setDistrictOptions(regState === "Gujarat" ? GUJARAT_DISTRICTS : []);
        }
      } finally {
        if (active) setDistrictLoading(false);
      }
    }
    loadDistricts();
    return () => {
      active = false;
    };
  }, [regState, registrationConfig.district]);
  const totalStats = useMemo2(() => {
    const stateRegisteredPacs = stateStats.reduce(
      (sum, row) => sum + publicCount(row.pacsRegistered, row.pacs),
      0
    );
    const stateTotalProjects = stateStats.reduce(
      (sum, row) => sum + publicCount(row.projects),
      0
    );
    const stateCapacityMt = stateStats.reduce(
      (sum, row) => sum + parseMetricNumber(row.capacityMt),
      0
    );
    return {
      states: stateStats.length,
      branches: publicCount(
        publicSummary?.approvedBranches,
        publicSummary?.stateBranches,
        publicSummary?.branches,
        branchCount
      ),
      registeredPacs: publicCount(
        publicSummary?.pacsRegistered,
        publicSummary?.registeredPacs,
        publicSummary?.pacs,
        stateRegisteredPacs
      ),
      pacs: publicCount(
        publicSummary?.pacsRegistered,
        publicSummary?.registeredPacs,
        publicSummary?.pacs,
        stateRegisteredPacs
      ),
      approvedPacs: publicCount(
        publicSummary?.pacsApproved,
        publicSummary?.approvedPacs,
        publicSummary?.approvedVisiblePacs
      ),
      appliedPacs: publicCount(
        publicSummary?.applicationsSubmitted,
        publicSummary?.pacsApplied,
        publicSummary?.appliedPacs
      ),
      allocatedPacs: publicCount(
        publicSummary?.pacsAllocated,
        publicSummary?.activeAllocations
      ),
      projects: publicCount(
        publicSummary?.projectsTotal,
        publicSummary?.totalProjects,
        publicSummary?.projects,
        stateTotalProjects
      ),
      implementationProjects: publicCount(
        publicSummary?.workStartedProjects,
        publicSummary?.implementationStartedProjects,
        publicSummary?.projectsImplementationStarted,
        publicSummary?.implementationProjects,
        publicSummary?.activeProjects
      ),
      capacityMt: capacityFrom(publicSummary || {}) || parseMetricNumber(stateCapacityMt)
    };
  }, [stateStats, branchCount, publicSummary]);
  const selectedRoleData = ROLE_OPTIONS.find(([value]) => value === selectedRole) || ROLE_OPTIONS[0];
  const formatCapacity = (mt) => {
    const n = parseMetricNumber(mt);
    if (n >= 1e7) return `${(n / 1e7).toFixed(2)} Cr MT`;
    if (n >= 1e5) return `${(n / 1e5).toFixed(2)} LMT`;
    return `${n.toLocaleString("en-IN")} MT`;
  };
  const formatMoney = (value) => {
    const amount = parseMetricNumber(value);
    if (!amount) return "—";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0
    }).format(amount);
  };
  const formatLiveDate = (value) => {
    const millis = tenderDateMillis(value);
    if (!Number.isFinite(millis)) return "—";
    return new Intl.DateTimeFormat("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true }).format(millis);
  };
  const getVideoEmbedUrl = (url = "") => {
    const value = String(url).trim();
    const youtube = value.match(
      /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/
    );
    return youtube ? `https://www.youtube.com/embed/${youtube[1]}` : "";
  };
  const redirectByRole = (profile) => {
    window.location.href = ROLE_ROUTES[normalizeRole(profile?.role)] || "/nafed";
  };
  const normalizeRole = (roleValue) => {
    const value = String(roleValue || "").trim().toUpperCase();
    if (value === "NAFED_HEAD") return "HEAD_OFFICE";
    if (value === "STATE_REG") return "STATE_REGISTRAR";
    if (value === "DISTRICT_REG") return "DISTRICT_REGISTRAR";
    if (value === "PACS_SOCIETY" || value === "PACS / SOCIETY") return "PACS";
    return value;
  };
  const getRoleLabel = (roleValue) => ROLE_OPTIONS.find(([value]) => value === normalizeRole(roleValue))?.[1] || roleValue || "Unknown Role";
  const validateSelectedRole = async (profile) => {
    if (!profile) {
      await signOut(auth);
      alert("NAFED user profile nahi mila. Please register first.");
      return false;
    }
    if (normalizeRole(profile.role) !== normalizeRole(selectedRole)) {
      await signOut(auth);
      alert("Access denied. The credentials do not match the selected portal.");
      return false;
    }
    if (String(profile.status || "").toUpperCase() !== "APPROVED") {
      await signOut(auth);
      alert(
        `Aapka registration abhi approval pending hai. Current status: ${profile.status || "PENDING"}`
      );
      return false;
    }
    if (profile.isActive === false) {
      await signOut(auth);
      alert("Aapka account inactive hai. Administrator se contact karein.");
      return false;
    }
    return true;
  };
  const openLoginForRole = (roleValue) => {
    setSelectedRole(roleValue || "");
    setLoginMode(roleValue ? "PASSWORD" : "ROLE");
    setShowLoginPopup(true);
  };
  const openRegisterForRole = (roleValue = "PACS") => {
    setRole(roleValue);
    setShowRegisterPopup(true);
  };
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return alert("Email aur password enter karo");
    try {
      setLoading(true);
      const userCred = await signInWithEmailAndPassword(auth, email, password);
      const snap = await getDoc(doc(db2, "nafed_users", userCred.user.uid));
      const user = snap.exists() ? snap.data() : null;
      const isValidRole = await validateSelectedRole(user);
      if (!isValidRole) return;
      redirectByRole(user);
    } catch (err) {
      console.error(err);
      alert("Login failed");
    } finally {
      setLoading(false);
    }
  };
  const handleRegister = async (e) => {
    e.preventDefault();
    const missingLocation = registrationConfig.state && !regState || registrationConfig.district && !regDistrict || registrationConfig.taluka && !regTaluka.trim() || registrationConfig.village && !regVillage.trim() || registrationConfig.address && !regAddress.trim();
    if (!role || !name.trim() || !organizationName.trim() || !registrationNumber.trim() || !designation.trim() || !email.trim() || !mobile.trim() || !password || !confirmPassword || missingLocation) {
      return alert("Please complete all mandatory registration fields.");
    }
    if (mobile.length !== 10)
      return alert("Please enter a valid 10-digit mobile number.");
    if (password.length < 8)
      return alert("Password must contain at least 8 characters.");
    if (password !== confirmPassword)
      return alert("Password and Confirm Password do not match.");
    try {
      setLoading(true);
      const cred = await createUserWithEmailAndPassword(
        auth,
        email.trim().toLowerCase(),
        password
      );
      const pendingStatus = ROLE_PENDING_STATUS[role] || "PENDING_ROLE_APPROVAL";
      await setDoc(doc(db2, "nafed_users", cred.user.uid), {
        uid: cred.user.uid,
        name: name.trim(),
        email: email.trim(),
        mobile: mobile.trim(),
        role,
        vendorServiceType: role.endsWith("_VENDOR") ? role.replace("_VENDOR", "") : "",
        vendorCategory: role.endsWith("_VENDOR") ? "NAFED_TECHNICAL_VENDOR" : "",
        nationalVendorVisibility: role.endsWith("_VENDOR"),
        visibleToAllApprovedPmcs: role.endsWith("_VENDOR"),
        organizationName: organizationName.trim(),
        registrationNumber: registrationNumber.trim().toUpperCase(),
        designation: designation.trim(),
        jurisdictionLevel: registrationConfig.scope,
        state: registrationConfig.state ? regState : "",
        district: registrationConfig.district ? regDistrict : "",
        taluka: registrationConfig.taluka ? regTaluka.trim() : "",
        village: registrationConfig.village ? regVillage.trim() : "",
        address: registrationConfig.address ? regAddress.trim() : "",
        organizationStateKey: `${role}_${organizationName.trim().toLowerCase()}_${registrationConfig.state ? regState.toLowerCase() : "national"}`,
        status: pendingStatus,
        registrationStatus: pendingStatus,
        approvalStage: "AWAITING_HIGHER_AUTHORITY",
        roleApprovalStatus: "PENDING",
        shineAgriStatus: role === "HEAD_OFFICE" ? "PENDING" : "NOT_REQUIRED",
        preVerificationStatus: role === "HEAD_OFFICE" ? "PENDING_SHINE_AGRI" : "NOT_REQUIRED",
        finalApproverRole: approvalConfig.approverRole,
        requiredApproverRole: approvalConfig.approverRole,
        approvalAuthorityRole: approvalConfig.approverRole,
        approvalAuthorityLabel: approvalConfig.approverLabel,
        approvalChain: [approvalConfig.approverRole],
        approvedByUid: "",
        approvedByRole: "",
        approvedAt: null,
        isActive: false,
        nafedProject: role === "PACS",
        registrationPortal: role === "PACS" ? "NAFED_PACS_REGISTRATION" : "NAFED_ROLE_REGISTRATION",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      await signOut(auth);
      alert(
        `Registration submitted successfully. Final approval is pending with ${approvalConfig.approverLabel}.`
      );
      setShowRegisterPopup(false);
      setShowLoginPopup(true);
      setSelectedRole(role);
      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.error(err);
      alert(err.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };
  const handleForgotPassword = async () => {
    if (!email) return alert("Registered email enter karo.");
    try {
      setLoading(true);
      await sendPasswordResetEmail(auth, email);
      alert("Password reset link email par bhej diya.");
      setLoginMode("PASSWORD");
    } catch (err) {
      console.error(err);
      alert(err.message || "Password reset failed");
    } finally {
      setLoading(false);
    }
  };
  const sendOtp = async () => {
    if (!mobile || mobile.length !== 10)
      return alert("10 digit mobile number enter karo.");
    try {
      setLoading(true);
      if (!window.nafedRecaptchaVerifier) {
        window.nafedRecaptchaVerifier = new RecaptchaVerifier(
          auth,
          "nafed-recaptcha",
          { size: "invisible" }
        );
      }
      const result = await signInWithPhoneNumber(
        auth,
        `+91${mobile}`,
        window.nafedRecaptchaVerifier
      );
      setConfirmation(result);
      alert("OTP sent successfully.");
    } catch (err) {
      console.error(err);
      alert(err.message || "OTP send failed.");
    } finally {
      setLoading(false);
    }
  };
  const verifyOtp = async () => {
    if (!confirmation || !otp) return alert("OTP enter karo.");
    try {
      setLoading(true);
      const otpResult = await confirmation.confirm(otp);
      const q1 = query(
        collection2(db2, "nafed_users"),
        where("mobile", "==", mobile),
        where("status", "==", "APPROVED")
      );
      const snap = await getDocs(q1);
      if (snap.empty) {
        await signOut(auth);
        return alert("Approved user profile mobile number se nahi mila.");
      }
      const matchingDoc = snap.docs.find(
        (item) => normalizeRole(item.data().role) === normalizeRole(selectedRole) && (!item.data().uid || item.data().uid === otpResult.user.uid)
      );
      if (!matchingDoc) {
        await signOut(auth);
        alert("Access denied. The verified mobile account does not match the selected approved profile.");
        return;
      }
      const profile = matchingDoc.data();
      const isValidRole = await validateSelectedRole(profile);
      if (!isValidRole) return;
      redirectByRole(profile);
    } catch (err) {
      console.error(err);
      alert(err.message || "OTP verification failed.");
    } finally {
      setLoading(false);
    }
  };
  const sliderData = stateStats;
  return <>
      <style>{`
        html,body,#root{margin:0;padding:0;width:100%;min-height:100%;overflow-x:hidden}
        *{box-sizing:border-box}
        .nafed-page{width:100%;min-height:100vh;background:linear-gradient(135deg,#f6fbff,#edf5ff 48%,#fff);color:#061d45;font-family:Inter,"Segoe UI",Arial,sans-serif;display:flex;flex-direction:column}
        .nafed-page>.gov-strip,.nafed-page>.nafed-header,.nafed-page>.live-command,.nafed-page>.main,.nafed-page>.footer{display:none!important}
        .nafed-header{height:76px;padding:0 38px;background:rgba(255,255,255,.94);backdrop-filter:blur(22px);border-bottom:1px solid rgba(10,60,137,.08);box-shadow:0 14px 35px rgba(6,29,69,.08);display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;z-index:20}
        .brand{display:flex;align-items:center;gap:14px}
        .logo{font-size:34px;font-weight:950;letter-spacing:-1.5px;color:#0b8a3b}
        .brand h1{margin:0;font-size:22px;font-weight:950;line-height:1.05}
        .brand p{margin:3px 0 0;color:#0b8a3b;font-size:14px;font-weight:900}
        .nav{display:flex;gap:10px;align-items:center}
        .nav a,.top-btn{text-decoration:none;color:#061d45;font-size:13px;font-weight:850;padding:10px 13px;border-radius:12px;white-space:nowrap;border:0;background:transparent;cursor:pointer}
        .nav a:hover,.top-btn:hover{background:#eef5ff;color:#0a4faf}
        .top-login{color:white!important;background:linear-gradient(135deg,#05265b,#0b3f8d)!important;box-shadow:0 12px 26px rgba(5,38,91,.24)}
        .top-register{color:white!important;background:linear-gradient(135deg,#0b8a3b,#10a64e)!important;box-shadow:0 12px 26px rgba(11,138,59,.22)}
        .main{padding:20px 36px 10px}
        .live-command{margin:0 36px;background:#092b58;color:#fff;border-bottom:3px solid #e5a12b;display:grid;grid-template-columns:auto 1fr auto;align-items:center;min-height:48px;overflow:hidden;box-shadow:0 12px 30px rgba(5,38,91,.16)}
        .live-command-label{align-self:stretch;display:flex;align-items:center;gap:9px;padding:0 18px;background:#087c3e;font-size:11px;font-weight:950;letter-spacing:.1em;white-space:nowrap}.live-command-label i{width:9px;height:9px;border-radius:50%;background:#fff;box-shadow:0 0 0 5px rgba(255,255,255,.18);animation:livePulse 1.5s infinite}@keyframes livePulse{50%{opacity:.35;transform:scale(.75)}}
        .live-command-track{overflow:hidden;white-space:nowrap}.live-command-runner{display:inline-flex;align-items:center;gap:42px;padding-left:100%;animation:activityTicker 38s linear infinite}.live-command-track:hover .live-command-runner{animation-play-state:paused}@keyframes activityTicker{to{transform:translateX(-100%)}}
        .live-command-item{display:inline-flex;gap:9px;align-items:center;font-size:12px}.live-command-item b{color:#fbd38d}.live-command-item em{font-style:normal;color:#c7d8ee}.live-command-state{padding:0 16px;font-size:10px;font-weight:900;color:#d8e7f7;white-space:nowrap}
        .public-monitor{width:calc(100% - 72px);margin:0 auto 24px;display:grid;grid-template-columns:1.05fr 1.95fr;gap:18px}.monitor-panel{background:#fff;border:1px solid #ccd9e8;border-radius:20px;box-shadow:0 16px 36px rgba(6,29,69,.09);overflow:hidden}.monitor-head{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:16px 18px;border-bottom:1px solid #dbe4ef;background:#f8fbff}.monitor-head h3{margin:0;font-size:16px;font-weight:950}.monitor-head p{margin:4px 0 0;color:#64748b;font-size:10px;font-weight:750}.live-badge{padding:6px 10px;border-radius:999px;background:#dcfce7;color:#166534;border:1px solid #86efac;font-size:9px;font-weight:950;white-space:nowrap}.live-badge.waiting{background:#fff7ed;color:#9a3412;border-color:#fed7aa}
        .activity-list{padding:6px 16px 12px;max-height:300px;overflow:auto}.activity-row{display:grid;grid-template-columns:42px 1fr auto;gap:11px;align-items:center;padding:12px 0;border-bottom:1px solid #e8eef5}.activity-row:last-child{border-bottom:0}.activity-mark{width:38px;height:38px;border-radius:12px;display:grid;place-items:center;background:#e7f7ed;color:#087c3e;font-size:17px;font-weight:950}.activity-row b{display:block;font-size:12px;color:#092b58}.activity-row span{display:block;margin-top:4px;color:#65758b;font-size:10px}.activity-row time{font-size:9px;color:#74849a;font-weight:850;white-space:nowrap}.empty-live{padding:30px 18px;text-align:center;color:#64748b;font-size:12px}
        .tender-grid{padding:14px;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}.tender-card{border:1px solid #cfdae7;border-radius:14px;padding:13px;background:#fff;position:relative}.tender-card:before{content:"";position:absolute;left:0;top:12px;bottom:12px;width:3px;background:#e6a12b}.tender-top{display:flex;align-items:start;justify-content:space-between;gap:8px}.tender-top strong{font-size:11px;color:#092b58}.tender-status{padding:4px 7px;background:#e7f7ed;color:#087c3e;border-radius:999px;font-size:8px;font-weight:950}.tender-card h4{margin:9px 0 8px;font-size:12px;line-height:1.4;color:#172b4d}.tender-meta{display:grid;grid-template-columns:repeat(2,1fr);gap:6px;color:#64748b;font-size:9px}.tender-meta b{display:block;color:#102a4f;margin-top:2px}.tender-link{display:inline-flex;margin-top:10px;color:#0757b7;font-size:10px;font-weight:950;text-decoration:none}.all-tender-link{border:1px solid #0b55b7;background:#fff;color:#0757b7;padding:7px 10px;border-radius:9px;font-size:10px;font-weight:950;text-decoration:none}
        .highlight-section{width:calc(100% - 72px);margin:0 auto 20px;background:#fff;border:1px solid #ccd9e8;border-radius:20px;overflow:hidden;box-shadow:0 16px 36px rgba(6,29,69,.09)}.highlight-head{padding:16px 18px;background:linear-gradient(100deg,#092b58,#0b6b43);color:#fff;display:flex;align-items:center;justify-content:space-between;gap:16px}.highlight-head h3{margin:0;font-size:16px}.highlight-head p{margin:4px 0 0;color:#d9f3e4;font-size:10px}.highlight-head span{padding:6px 10px;border:1px solid rgba(255,255,255,.4);border-radius:999px;font-size:9px;font-weight:950}.highlight-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px;padding:16px}.highlight-card{border:1px solid #d5e0ec;border-radius:15px;overflow:hidden;background:#f8fbff}.highlight-media{aspect-ratio:16/9;background:#071f45;display:grid;place-items:center}.highlight-media iframe,.highlight-media video{width:100%;height:100%;border:0;object-fit:cover}.highlight-link{color:#fff;text-decoration:none;font-weight:900;font-size:12px}.highlight-info{padding:12px}.highlight-info h4{margin:0;color:#092b58;font-size:12px;line-height:1.4}.highlight-info p{margin:6px 0 0;color:#64748b;font-size:10px;line-height:1.45}.highlight-info small{display:block;margin-top:8px;color:#087c3e;font-weight:900}.highlight-empty{padding:28px;text-align:center;color:#64748b;font-size:11px;grid-column:1/-1}
        .hero-shell{width:100%;height:auto;background:rgba(255,255,255,.66);border:1px solid rgba(255,255,255,.96);border-radius:30px;padding:8px;box-shadow:0 28px 70px rgba(6,29,69,.16)}
        .hero{width:100%;height:auto;border-radius:23px;overflow:hidden;background:#dce8f5;display:block}
        .hero img{width:100%;height:auto;max-height:none;object-fit:contain;object-position:center;display:block}
        .stats{width:min(1180px,calc(100% - 80px));margin:-44px auto 24px;position:relative;z-index:4;background:linear-gradient(135deg,#05265b,#0a3c89);color:white;border-radius:20px;display:grid;grid-template-columns:repeat(5,1fr);padding:15px 18px;box-shadow:0 18px 36px rgba(5,38,91,.25)}
        .stats div{text-align:center;border-right:1px solid rgba(255,255,255,.2)}
        .stats div:last-child{border-right:none}
        .stats b{display:block;font-size:25px;font-weight:950;line-height:1.1}
        .stats span{display:block;margin-top:4px;font-size:11px;opacity:.9}
        .overview-title{width:calc(100% - 72px);margin:0 auto 10px;font-size:17px;font-weight:950;color:#061d45}
        .state-slider{width:calc(100% - 72px);margin:0 auto;overflow:hidden;border-radius:22px;padding:2px 0 20px}
        .state-track{display:flex;gap:14px;width:max-content;animation:stateScroll 145s linear infinite}
        .state-track:hover{animation-play-state:paused}
        @keyframes stateScroll{from{transform:translateX(0)}to{transform:translateX(-50%)}}
        .state-card{width:240px;min-height:94px;flex:0 0 auto;border-radius:18px;padding:13px 16px;text-align:center;background:white;border:1px solid rgba(210,226,245,.95);box-shadow:0 14px 28px rgba(6,29,69,.08)}
        .state-card h4{margin:0 0 7px;color:#0a3c89;font-size:14px;font-weight:950}
        .state-card p{margin:2px 0;font-size:11px;font-weight:850}
        .state-card b{color:#0b8a3b}
        .login-as{padding:8px 36px 28px}
        .login-as h3{margin:0 0 14px;text-align:center;font-size:19px;font-weight:950}
        .roles{display:grid;grid-template-columns:repeat(11,minmax(0,1fr));gap:12px}
        .role{height:86px;border-radius:22px;background:white;border:1px solid rgba(210,226,245,.95);box-shadow:0 14px 28px rgba(6,29,69,.09);display:flex;flex-direction:column;justify-content:center;align-items:center;transition:.22s;cursor:pointer}
        .role:hover,.role.active{transform:translateY(-5px);border-color:#16a34a;box-shadow:0 22px 40px rgba(6,29,69,.16)}
        .role-icon{font-size:25px;margin-bottom:6px}
        .role span{font-size:11px;font-weight:950;text-align:center}
        .hint{width:max-content;max-width:100%;margin:16px auto 0;background:white;border:1px solid #dbeafe;border-radius:12px;padding:9px 16px;font-size:13px;font-weight:850;box-shadow:0 10px 22px rgba(6,29,69,.08)}
        .footer{height:52px;padding:0 36px;background:linear-gradient(135deg,#05265b,#0a3c89);color:white;display:flex;align-items:center;justify-content:space-between;font-size:12px}
        .overlay{position:fixed;inset:0;z-index:999;background:rgba(2,6,23,.62);backdrop-filter:blur(8px);display:flex;align-items:center;justify-content:center;padding:20px}
        .modal{width:min(520px,100%);max-height:92vh;overflow:auto;background:white;border-radius:30px;box-shadow:0 40px 110px rgba(0,0,0,.35);border:1px solid rgba(255,255,255,.9)}
        .reg-modal{width:min(820px,100%)}
        .modal-head{padding:22px 26px;background:linear-gradient(135deg,#05265b,#0a3c89);color:#fff;border-radius:30px 30px 0 0;display:flex;justify-content:space-between;align-items:center;gap:14px}
        .modal-head h2{margin:6px 0 0;font-size:24px;font-weight:950}
        .modal-close{width:40px;height:40px;border-radius:50%;border:0;background:rgba(255,255,255,.16);color:#fff;font-size:22px;cursor:pointer}
        .modal-body{padding:26px}
        .role-badge{width:68px;height:68px;border-radius:50%;margin:0 auto 12px;background:#dcfce7;border:2px solid #16a34a;display:flex;align-items:center;justify-content:center;font-size:32px}
        .modal-title{text-align:center;margin:0 0 22px;font-size:24px;font-weight:950}
        .login-role-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:11px}.login-role-option{min-height:76px;border:1px solid #d5e0ec;border-radius:15px;background:#f8fbff;padding:12px;display:grid;grid-template-columns:38px 1fr auto;align-items:center;gap:10px;text-align:left;cursor:pointer;transition:.2s}.login-role-option:hover{transform:translateY(-2px);border-color:#15924b;background:#f0faf4;box-shadow:0 12px 24px rgba(6,29,69,.1)}.login-role-option span{width:38px;height:38px;border-radius:11px;background:#e5f5eb;display:grid;place-items:center;font-size:19px}.login-role-option b{font-size:11px;color:#092b58}.login-role-option i{font-size:9px;color:#087c3e;font-style:normal;font-weight:950;white-space:nowrap}
        .input,.select{width:100%;height:52px;margin-bottom:14px;padding:0 18px;border-radius:16px;border:1px solid #d6e1ef;background:#f8fbff;font-size:13px;outline:none}
        .input:focus,.select:focus{background:white;border-color:#0b56b3;box-shadow:0 0 0 4px rgba(11,86,179,.1)}
        .main-btn{width:100%;height:52px;border:none;border-radius:16px;color:white;background:linear-gradient(135deg,#05265b,#0b3f8d);box-shadow:0 16px 32px rgba(5,38,91,.24);font-size:15px;font-weight:950;cursor:pointer}
        .main-btn:disabled{opacity:.7;cursor:not-allowed}
        .green-btn{background:linear-gradient(135deg,#0b8a3b,#10a64e)!important;box-shadow:0 16px 32px rgba(11,138,59,.24)!important}
        .outline-btn{background:white!important;color:#062c65!important;border:1px solid #0b55b7!important;box-shadow:none!important}
        .link-btn{border:0;background:transparent;color:#005bd4;font-size:12px;font-weight:900;cursor:pointer;padding:0}
        .or{text-align:center;margin:15px 0;color:#64748b;font-weight:900}
        .login-row{display:flex;justify-content:space-between;align-items:center;margin:2px 0 16px;font-size:12px}
        .reg-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px}
        .reg-field.full{grid-column:1/-1}
        .reg-field label{font-size:11px;font-weight:950;color:#64748b;letter-spacing:.08em}
        .reg-field input,.reg-field select{width:100%;height:52px;margin-top:6px;border-radius:16px;border:1px solid #d6e1ef;padding:0 16px;background:#fff;outline:none}
        .role-scope-note{padding:13px 15px;border:1px solid #b9d8c5;border-radius:14px;background:#eff9f3}.role-scope-note b,.role-scope-note span,.role-scope-note em{display:block}.role-scope-note b{color:#08743a;font-size:12px}.role-scope-note span{margin-top:5px;color:#527064;font-size:11px;line-height:1.5}.role-scope-note em{width:max-content;max-width:100%;margin-top:9px;padding:5px 8px;border-radius:999px;background:#d9f1e2;color:#075c31;font-size:9px;font-style:normal;font-weight:900}
        .reg-note{margin-top:20px;padding:16px;border-radius:18px;background:#fff7ed;border:1px solid #fed7aa;color:#9a3412;font-size:13px;line-height:1.6;font-weight:700}
        .reg-actions{display:flex;justify-content:space-between;gap:12px;margin-top:22px}
        .cancel-btn{flex:1;height:52px;border-radius:16px;border:1px solid #d6e1ef;background:#fff;color:#061d45;font-weight:950;cursor:pointer}
        .submit-btn{flex:1;height:52px;border-radius:16px;border:0;background:linear-gradient(135deg,#0b8a3b,#10a64e);color:#fff;font-weight:950;cursor:pointer;box-shadow:0 18px 35px rgba(11,138,59,.25)}
        @media(max-width:1200px){.roles{grid-template-columns:repeat(4,1fr)}.nav{flex-wrap:wrap;justify-content:center}.hero-shell{height:auto}.public-monitor{grid-template-columns:1fr}.highlight-grid{grid-template-columns:repeat(2,1fr)}}

        /* Desktop/laptop: keep the complete landing page inside one viewport */
        @media (min-width: 901px) {
          .nafed-page {
            height: 100vh;
            height: 100dvh;
            min-height: 620px;
            overflow: hidden;
            display: grid;
            grid-template-rows: 64px minmax(0, 1fr) 166px 40px;
          }

          .nafed-header {
            height: 64px;
            min-height: 64px;
            padding: 0 26px;
          }

          .logo { font-size: 30px; }
          .brand h1 { font-size: 18px; }
          .brand p { font-size: 11px; }
          .nav { flex-wrap: nowrap; gap: 4px; }
          .nav a,
          .top-btn { padding: 8px 9px; font-size: 11px; }

          .main {
            min-height: 0;
            padding: 8px 24px 0;
            display: grid;
            grid-template-rows: minmax(0, 1fr) 58px 26px 96px;
            overflow: hidden;
          }

          .hero-shell {
            width: 100%;
            min-height: 0;
            height: 100%;
            padding: 6px;
            border-radius: 22px;
          }

          .hero {
            width: 100%;
            height: 100%;
            min-height: 0;
            border-radius: 17px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .hero img {
            width: 100%;
            height: 100%;
            object-fit: contain;
            object-position: center;
          }

          .stats {
            align-self: center;
            width: min(1160px, calc(100% - 80px));
            height: 58px;
            margin: -16px auto 0;
            padding: 8px 15px;
          }

          .stats b { font-size: 21px; }
          .stats span { margin-top: 2px; font-size: 10px; }

          .overview-title {
            width: calc(100% - 48px);
            margin: 2px auto 0;
            font-size: 15px;
          }

          .state-slider {
            width: calc(100% - 48px);
            height: 96px;
            margin: 0 auto;
            padding: 0;
          }

          .state-track { gap: 10px; }
          .state-card {
            width: 220px;
            min-height: 84px;
            height: 84px;
            padding: 10px 12px;
            border-radius: 14px;
          }

          .state-card h4 { margin-bottom: 5px; font-size: 13px; }
          .state-card p { margin: 2px 0; font-size: 10px; }

          .login-as {
            min-height: 0;
            height: 166px;
            padding: 8px 24px 8px;
            overflow: hidden;
          }

          .login-as h3 { margin: 0 0 8px; font-size: 18px; }
          .roles {
            grid-template-columns: repeat(11, minmax(0, 1fr));
            gap: 9px;
          }

          .role { height: 86px; border-radius: 16px; }
          .role-icon { margin-bottom: 5px; font-size: 23px; }
          .role span { font-size: 10px; }
          .hint {
            margin-top: 7px;
            padding: 6px 12px;
            border-radius: 8px;
            font-size: 11px;
          }

          .footer {
            height: 40px;
            padding: 0 24px;
            font-size: 11px;
          }
        }

        /* Mobile/tablet remains scrollable and readable */
        @media (max-width: 900px) {
          .nafed-page { min-height: 100vh; overflow-x: hidden; overflow-y: auto; }
          .nafed-header { height: auto; min-height: 70px; padding: 12px 16px; flex-wrap: wrap; gap: 10px; }
          .main { padding: 14px 12px 6px; }
          .roles { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .login-as { padding: 10px 12px 22px; }
          .footer { height: auto; min-height: 52px; padding: 12px; gap: 8px; flex-wrap: wrap; }
        }

        /* Premium portal layout overrides */
        .nafed-page{display:block!important;height:auto!important;min-height:100vh!important;overflow:visible!important;background:#f3f7fb!important}
        .nafed-header{height:76px!important;min-height:76px!important;padding:0 max(28px,calc((100vw - 1540px)/2))!important;background:rgba(255,255,255,.96)!important;border-bottom:1px solid #dce6f0!important;box-shadow:0 8px 30px rgba(15,45,76,.07)!important}
        .brand{gap:12px}.logo-box{width:112px;height:52px;display:flex;align-items:center;justify-content:center;padding:3px 8px;border:1px solid #e0e8ef;border-radius:10px;background:#fff}.logo-box img{display:block;max-width:100%;max-height:100%;object-fit:contain}.brand h1{font-size:18px!important;letter-spacing:-.02em}.brand p{font-size:10px!important;letter-spacing:.18em;text-transform:uppercase}
        .nav{gap:2px!important;flex-wrap:nowrap!important}.nav a,.top-btn{padding:9px 11px!important;font-size:11px!important;border-radius:9px!important}.top-login,.top-register{margin-left:5px}
        .main{display:block!important;min-height:auto!important;overflow:visible!important;padding:22px max(28px,calc((100vw - 1540px)/2)) 0!important}
        .hero-shell{height:clamp(390px,43vw,590px)!important;min-height:390px!important;padding:7px!important;border:1px solid #dce7f1!important;border-radius:24px!important;background:#fff!important;box-shadow:0 25px 65px rgba(15,45,76,.14)!important}
        .hero{position:relative!important;height:100%!important;display:block!important;border-radius:18px!important;background:#d7e8f7!important}
        .hero:after{display:none!important}
        .hero-slides{position:absolute;inset:0}.hero-slides .slide{position:absolute;inset:0;width:100%!important;height:100%!important;object-fit:cover!important;object-position:center!important;opacity:0;transform:scale(1.025);transition:opacity .8s ease,transform 7s ease}.hero-slides .slide.active{opacity:1;transform:scale(1)}
        .slider-arrow{position:absolute;z-index:5;top:50%;width:40px;height:40px;transform:translateY(-50%);border:1px solid rgba(255,255,255,.85);border-radius:10px;background:rgba(5,32,67,.54);color:#fff;font-size:27px;line-height:1;cursor:pointer;opacity:.25;backdrop-filter:blur(6px);transition:.2s}.hero:hover .slider-arrow{opacity:1}.slider-arrow:hover{background:#075b3d}.slider-arrow.previous{left:14px}.slider-arrow.next{right:14px}.slider-dots{position:absolute;z-index:5;left:50%;bottom:15px;display:flex;gap:6px;padding:7px 9px;border-radius:999px;background:rgba(5,32,67,.38);transform:translateX(-50%);backdrop-filter:blur(6px)}.slider-dots button{width:7px;height:7px;padding:0;border:0;border-radius:999px;background:rgba(255,255,255,.62);cursor:pointer;transition:.25s}.slider-dots button.active{width:25px;background:#fff}
        .stats{width:min(1120px,calc(100% - 70px))!important;height:auto!important;min-height:78px!important;margin:-31px auto 24px!important;padding:14px 18px!important;border:1px solid rgba(255,255,255,.22)!important;border-radius:16px!important;background:linear-gradient(115deg,#092c5d,#0b4385 62%,#087342)!important;box-shadow:0 20px 45px rgba(8,44,89,.24)!important}.stats b{font-size:23px!important}.stats span{font-size:9px!important;letter-spacing:.08em;text-transform:uppercase}
        .overview-title{display:flex!important;align-items:flex-end;justify-content:space-between;width:100%!important;margin:0 0 11px!important;padding:0 4px!important}.overview-title span{font-size:17px;font-weight:950}.overview-title small{color:#718096;font-size:10px;font-weight:700}
        .state-slider{width:100%!important;height:112px!important;margin:0!important;padding:0 0 14px!important}.state-track{gap:10px!important;animation-duration:165s!important}.state-card{width:220px!important;height:94px!important;min-height:94px!important;padding:13px!important;border:1px solid #dce5ee!important;border-radius:12px!important;box-shadow:0 8px 20px rgba(15,45,76,.06)!important}.state-card h4{font-size:12px!important}.state-card p{font-size:9px!important}
        .login-as{height:auto!important;min-height:auto!important;margin:8px max(28px,calc((100vw - 1540px)/2)) 28px!important;padding:22px!important;overflow:visible!important;border:1px solid #d8e3ed;border-radius:20px;background:#fff;box-shadow:0 18px 45px rgba(15,45,76,.08)}
        .access-heading{display:flex;align-items:center;justify-content:space-between;gap:20px;margin-bottom:17px;padding-bottom:16px;border-bottom:1px solid #e3eaf1}.access-heading span{color:#148447;font-size:9px;font-weight:950;letter-spacing:.13em}.access-heading h3{margin:5px 0 3px!important;text-align:left!important;font-size:22px!important}.access-heading p{margin:0;color:#718096;font-size:11px}.access-heading button{padding:10px 14px;border:1px solid #158a49;border-radius:9px;background:#edf9f2;color:#08743a;font-size:10px;font-weight:950;cursor:pointer}
        .roles{grid-template-columns:repeat(7,minmax(0,1fr))!important;gap:10px!important}.role{position:relative;height:102px!important;padding:12px 6px;border:1px solid #dce5ee!important;border-radius:13px!important;box-shadow:none!important}.role:hover,.role.active{transform:translateY(-3px)!important;border-color:#15904d!important;background:#f0faf4!important;box-shadow:0 12px 28px rgba(15,87,53,.12)!important}.role.active:after{content:"SELECTED";position:absolute;right:7px;top:7px;padding:3px 5px;border-radius:999px;background:#128346;color:#fff;font-size:6px;font-weight:950;letter-spacing:.08em}.role-icon{font-size:24px!important}.role span{font-size:10px!important}.hint{width:100%!important;margin:12px 0 0!important;padding:8px!important;border:0!important;background:#f3f7fa!important;box-shadow:none!important;color:#607286;text-align:center;font-size:9px!important;letter-spacing:.04em}
        .footer{height:46px!important;min-height:46px!important;padding:0 max(28px,calc((100vw - 1540px)/2))!important;background:#071f45!important}
        @media(max-width:1100px){.roles{grid-template-columns:repeat(4,minmax(0,1fr))!important}.nav a{display:none}}
        @media(max-width:700px){.nafed-header{height:auto!important;min-height:68px!important;padding:10px 14px!important}.brand h1{font-size:13px!important}.brand p{font-size:8px!important}.logo-box{width:78px;height:44px}.nav{margin-left:auto}.top-btn{padding:8px!important}.main{padding:12px!important}.hero-shell{height:300px!important;min-height:300px!important}.hero-slides .slide{object-position:center!important}.slider-arrow{display:none}.slider-dots{bottom:9px}.stats{grid-template-columns:repeat(2,1fr)!important;width:calc(100% - 24px)!important;margin:-14px auto 18px!important;gap:0;padding:8px!important}.stats div{padding:9px;border-right:0!important;border-bottom:1px solid rgba(255,255,255,.15)}.stats div:last-child{grid-column:1/-1}.overview-title small{display:none}.login-as{margin:8px 12px 20px!important;padding:15px!important}.access-heading{align-items:flex-start;flex-direction:column}.roles{grid-template-columns:repeat(2,minmax(0,1fr))!important}.footer{height:auto!important;padding:12px!important}.reg-grid{grid-template-columns:1fr}.reg-field.full{grid-column:auto}.login-role-grid{grid-template-columns:1fr}}
        @media(max-width:700px){.highlight-section{width:calc(100% - 24px)}.highlight-grid{grid-template-columns:1fr}.highlight-head{align-items:flex-start;flex-direction:column}}

        /* ===== PM ANNA BHANDARAN NATIONAL PORTAL 2026 ===== */
        .gov-strip{height:34px;padding:0 38px;background:#071b3c;color:#dbe8f7;display:flex;align-items:center;justify-content:space-between;font-size:10px;font-weight:750;letter-spacing:.02em}.gov-strip strong{color:#fff}.gov-links{display:flex;align-items:center;gap:16px}.gov-links span{opacity:.78}.gov-emblem{display:flex;align-items:center;gap:8px}.gov-emblem i{font-style:normal;color:#f4b63d}
        .nafed-header{height:86px;border-top:3px solid transparent;border-image:linear-gradient(90deg,#f08a24 0 33%,#fff 33% 66%,#168447 66%) 1;position:relative}.nafed-header:after{content:"";position:absolute;left:38px;right:38px;bottom:0;height:1px;background:linear-gradient(90deg,#f08a24,#edf2f7 48%,#168447)}
        .brand h1{font-size:21px;letter-spacing:-.02em}.brand p{font-size:10px;letter-spacing:.16em;text-transform:uppercase}.scheme-mark{padding-left:14px;border-left:1px solid #d9e2ec}.scheme-mark b,.scheme-mark span{display:block}.scheme-mark b{font-size:12px;color:#0a315e}.scheme-mark span{margin-top:3px;color:#b65c14;font-size:9px;font-weight:900;letter-spacing:.08em}
        .main{max-width:1480px;width:100%;margin:0 auto;padding:28px 36px 18px}.hero-shell{position:relative;padding:0;border-radius:24px;overflow:hidden;border:1px solid #b9cce0;background:#071e43;box-shadow:0 30px 70px rgba(4,27,63,.19)}.hero-shell:after{content:"";position:absolute;inset:0;background:linear-gradient(90deg,rgba(4,23,55,.96) 0%,rgba(4,27,63,.82) 31%,rgba(4,27,63,.2) 62%,rgba(4,27,63,.02));pointer-events:none}.hero{height:520px;border-radius:0}.hero img{width:100%;height:100%;object-fit:cover!important;object-position:center!important}.hero-copy{position:absolute;z-index:6;left:6%;top:50%;transform:translateY(-50%);width:min(560px,45%);color:#fff}.hero-kicker{display:inline-flex;align-items:center;gap:9px;padding:7px 10px;border:1px solid rgba(255,255,255,.33);border-radius:4px;background:rgba(15,110,63,.72);font-size:10px;font-weight:950;letter-spacing:.12em}.hero-copy h2{margin:18px 0 14px;font-size:48px;line-height:1.02;letter-spacing:-.045em}.hero-copy p{margin:0;max-width:520px;color:#dbe8f6;font-size:15px;line-height:1.75}.hero-actions{display:flex;gap:10px;margin-top:24px}.hero-actions button,.hero-actions a{min-height:42px;padding:0 17px;border-radius:7px;border:1px solid rgba(255,255,255,.56);font-size:11px;font-weight:950;text-decoration:none;display:inline-flex;align-items:center;justify-content:center;cursor:pointer}.hero-primary{background:#f3a62d!important;border-color:#f3a62d!important;color:#071e43!important}.hero-secondary{background:rgba(255,255,255,.08);color:#fff}.hero-assurance{display:flex;gap:18px;margin-top:26px;padding-top:17px;border-top:1px solid rgba(255,255,255,.23);color:#dbe8f6;font-size:9px;font-weight:850}.hero-assurance span:before{content:"✓";color:#7de0a0;margin-right:6px;font-weight:950}
        .stats{width:calc(100% - 90px);max-width:1200px;margin:-38px auto 30px;border:1px solid rgba(255,255,255,.17);border-radius:14px;background:linear-gradient(105deg,#0b2f68,#143f79 55%,#146842);box-shadow:0 22px 44px rgba(4,27,63,.24)}.stats div{position:relative}.stats div:before{content:attr(data-index);display:grid;place-items:center;width:23px;height:23px;margin:0 auto 6px;border:1px solid rgba(255,255,255,.3);border-radius:50%;color:#f6be52;font-size:8px;font-weight:950}.stats b{font-size:22px!important}.stats span{letter-spacing:.1em!important;font-size:8px!important}
        .section-eyebrow{color:#15814a;font-size:9px;font-weight:950;letter-spacing:.14em;text-transform:uppercase}.mission-intro{display:grid;grid-template-columns:1.3fr .7fr;gap:22px;margin:4px 0 22px}.mission-copy,.mission-status{border:1px solid #d3dfeb;background:#fff;border-radius:15px;padding:20px;box-shadow:0 12px 30px rgba(6,29,69,.06)}.mission-copy h3{margin:6px 0 9px;font-size:24px;color:#092b58}.mission-copy p{margin:0;color:#5e7086;line-height:1.7;font-size:12px}.mission-status{display:grid;grid-template-columns:repeat(2,1fr);gap:10px}.mission-status div{padding:12px;border-left:3px solid #168447;background:#f6faf8}.mission-status b{display:block;font-size:17px;color:#092b58}.mission-status span{display:block;margin-top:4px;font-size:8px;color:#6b7d91;font-weight:850;text-transform:uppercase;letter-spacing:.08em}
        .overview-title{padding:0 2px}.state-slider{border:1px solid #d5e0eb;border-radius:14px;background:#fff;padding:12px 0;box-shadow:0 12px 28px rgba(6,29,69,.05)}.state-card{border-radius:9px!important;box-shadow:none!important;border:1px solid #dbe4ee!important}.state-card h4{color:#0a315e!important}
        .command-title{margin:24px 0 12px;display:flex;align-items:end;justify-content:space-between}.command-title h3{margin:5px 0 0;color:#092b58;font-size:22px}.command-title p{margin:0;color:#6a7b8f;font-size:10px}.public-monitor{width:100%;grid-template-columns:1.05fr 1.55fr}.monitor-panel{border-radius:14px;box-shadow:0 12px 30px rgba(6,29,69,.07)}.monitor-head{border-top:3px solid #15814a}.activity-row{grid-template-columns:34px 1fr auto}.activity-mark{width:30px;height:30px;border-radius:50%;font-size:12px}.tender-card{border-radius:9px;background:#fbfdff}.highlight-section{width:100%;border-radius:14px}.highlight-head{background:linear-gradient(100deg,#082b59,#105538)}
        .quick-links{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin:0 0 26px}.quick-link{min-height:105px;border:1px solid #d3dfeb;border-top:3px solid #168447;border-radius:12px;background:#fff;padding:16px;text-decoration:none;box-shadow:0 10px 26px rgba(6,29,69,.06);transition:.2s}.quick-link:hover{transform:translateY(-3px);box-shadow:0 16px 34px rgba(6,29,69,.11)}.quick-link i{font-style:normal;font-size:21px}.quick-link b{display:block;margin-top:9px;color:#092b58;font-size:12px}.quick-link span{display:block;margin-top:5px;color:#6a7b8f;font-size:9px;line-height:1.45}.quick-link em{display:block;margin-top:8px;color:#168447;font-size:9px;font-style:normal;font-weight:950}
        /*
         * The new MissionPortalHome is the only public home screen.
         * Keep the legacy JSX below available for its login/registration
         * modals, but never render the retired duplicate page sections.
         */
        .nafed-page > .gov-strip,
        .nafed-page > .nafed-header,
        .nafed-page > .live-command,
        .nafed-page > .main,
        .nafed-page > .footer{display:none!important}
        .login-as{display:none!important}.footer{background:#071b3c!important;border-top:4px solid #168447!important;min-height:72px!important;color:#dbe8f7!important;padding:0 38px!important}.footer span:first-child:before{content:"PM Anna Bhandaran Yojana · ";color:#f5b948;font-weight:950}
        @media(max-width:1100px){.hero{height:460px}.hero-copy h2{font-size:39px}.mission-intro{grid-template-columns:1fr}.quick-links{grid-template-columns:repeat(2,1fr)}}
        @media(max-width:700px){.gov-strip{padding:0 14px}.gov-links span{display:none}.scheme-mark{display:none}.hero{height:480px!important}.hero-copy{left:8%;width:84%;top:47%}.hero-copy h2{font-size:34px}.hero-copy p{font-size:12px}.hero-shell:after{background:linear-gradient(90deg,rgba(4,23,55,.94),rgba(4,27,63,.55))}.hero-actions{flex-direction:column;align-items:flex-start}.hero-assurance{display:none}.stats{width:calc(100% - 22px)!important;margin:-28px auto 18px!important}.mission-status{grid-template-columns:1fr 1fr}.quick-links{grid-template-columns:1fr 1fr}.public-monitor{width:100%;grid-template-columns:1fr}.live-command{margin:0}.main{padding:14px}.command-title{align-items:flex-start;flex-direction:column;gap:5px}}
      `}</style>

      <div className="nafed-page">
        <MissionPortalHome
    summary={{
      ...publicSummary,
      states: totalStats.states,
      branches: totalStats.branches,
      pacs: totalStats.registeredPacs,
      pacsRegistered: totalStats.registeredPacs,
      registeredPacs: totalStats.registeredPacs,
      pacsApproved: totalStats.approvedPacs,
      approvedPacs: totalStats.approvedPacs,
      applicationsSubmitted: totalStats.appliedPacs,
      pacsApplied: totalStats.appliedPacs,
      appliedPacs: totalStats.appliedPacs,
      pacsAllocated: totalStats.allocatedPacs,
      activeAllocations: totalStats.allocatedPacs,
      projects: totalStats.projects,
      projectsTotal: totalStats.projects,
      totalProjects: totalStats.projects,
      activeProjects: totalStats.implementationProjects,
      implementationProjects: totalStats.implementationProjects,
      workStartedProjects: totalStats.implementationProjects,
      capacityMt: totalStats.capacityMt,
      totalCapacityMt: totalStats.capacityMt
    }}
    states={stateStats}
    activities={liveActivities}
    tenders={liveTenders}
    upcomingPacs={upcomingPacs}
    upcomingState={upcomingState}
    tenderState={tenderFeedState}
    highlights={videoHighlights}
    connection={liveConnection}
    formatCapacity={formatCapacity}
    formatMoney={formatMoney}
    formatDate={formatLiveDate}
    videoEmbed={getVideoEmbedUrl}
    onLogin={() => openLoginForRole("")}
    onRegister={() => openRegisterForRole("PACS")}
  />
        <div className="gov-strip">
          <div className="gov-emblem"><i>🇮🇳</i><strong>Government of India</strong><span> | Ministry of Cooperation</span></div>
          <div className="gov-links"><span>Skip to Main Content</span><span>हिन्दी</span><span>Accessibility</span><strong>National Public Portal</strong></div>
        </div>
        <header className="nafed-header">
          <div className="brand">
            <div className="logo-box">
              <img src={NAFED_LOGO} alt="NAFED" />
            </div>
            <div>
              <h1>World’s Largest Grain Storage Plan</h1>
              <p>National Digital Mission Portal</p>
            </div>
            <div className="scheme-mark"><b>PM Anna Bhandaran Yojana</b><span>COOPERATIVE GRAIN STORAGE MISSION</span></div>
          </div>

          <nav className="nav">
            <a href="/nafed">Home</a>
            <a href="/nafed/about">About Project</a>
            <a href="/nafed/guidelines">Guidelines</a>
            <a href="#legacy-tenders">Tenders</a>
            <a href="/nafed/help">Help Desk</a>
            <a href="/nafed/contact">Contact Us</a>
            <button
    className="top-btn top-login"
    onClick={() => openLoginForRole("")}
  >
              🔒 Login
            </button>
            <button
    className="top-btn top-register"
    onClick={() => openRegisterForRole("PACS")}
  >
              👤 Register
            </button>
          </nav>
        </header>

        <section className="live-command" aria-label="NAFED live activity feed">
          <div className="live-command-label">
            <i /> LIVE ACTIVITY
          </div>
          <div className="live-command-track">
            <div className="live-command-runner">
              {(liveActivities.length ? liveActivities : [
    {
      id: "welcome",
      type: "NOTICE",
      title: "NAFED Grain Storage Digital Portal is live",
      actor: "NAFED"
    },
    {
      id: "secure",
      type: "SECURITY",
      title: "Role-based approval and monitoring services are active",
      actor: "Portal Control"
    }
  ]).map((item) => <span className="live-command-item" key={item.id}>
                  <b>{item.type}</b>
                  <span>{item.title}</span>
                  <em>— {item.actor}</em>
                </span>)}
            </div>
          </div>
          <div className="live-command-state">
            {liveConnection === "LIVE" ? "● CONNECTED" : "PUBLIC VIEW"}
          </div>
        </section>

        <main className="main">
          <div className="hero-shell">
            <div className="hero">
              <div className="hero-slides">
                {HERO_SLIDES.map((slide, index) => <img
    className={activeSlide === index ? "slide active" : "slide"}
    key={slide.id}
    src={slide.image}
    alt={slide.title.replace("\n", " ")}
    onError={(event) => {
      if (event.currentTarget.src.endsWith(HERO_IMAGE)) return;
      event.currentTarget.src = HERO_IMAGE;
    }}
  />)}
              </div>
              {HERO_SLIDES.length > 1 && <>
                  <button
    className="slider-arrow previous"
    aria-label="Previous slide"
    onClick={() => setActiveSlide(
      (activeSlide - 1 + HERO_SLIDES.length) % HERO_SLIDES.length
    )}
  >
                    ‹
                  </button>
                  <button
    className="slider-arrow next"
    aria-label="Next slide"
    onClick={() => setActiveSlide((activeSlide + 1) % HERO_SLIDES.length)}
  >
                    ›
                  </button>
                  <div className="slider-dots">
                    {HERO_SLIDES.map((slide, index) => <button
    aria-label={`Open slide ${index + 1}`}
    className={activeSlide === index ? "active" : ""}
    key={slide.id}
    onClick={() => setActiveSlide(index)}
  />)}
                  </div>
                </>}
            </div>
            <div className="hero-copy">
              <span className="hero-kicker">🇮🇳 PRADHAN MANTRI ANNA BHANDARAN YOJANA</span>
              <h2>One Nation.<br />One Storage Network.</h2>
              <p>India’s integrated cooperative grain storage mission—connecting NAFED, State Governments, PACS, Banks and PMCs through one transparent, real-time digital command platform.</p>
              <div className="hero-actions">
                <button className="hero-primary" onClick={() => openLoginForRole("")}>Access Your Portal →</button>
                <a className="hero-secondary" href="/nafed/about">Explore the Mission</a>
              </div>
              <div className="hero-assurance"><span>Role-based security</span><span>Live national MIS</span><span>End-to-end monitoring</span></div>
            </div>
          </div>

          <div className="stats">
            <div data-index="01">
              <b>{totalStats.states}</b>
              <span>States</span>
            </div>
            <div data-index="02">
              <b>{totalStats.branches}</b>
              <span>Branches</span>
            </div>
            <div data-index="03">
              <b>{totalStats.pacs.toLocaleString("en-IN")}</b>
              <span>PACS</span>
            </div>
            <div data-index="04">
              <b>{totalStats.projects.toLocaleString("en-IN")}</b>
              <span>Projects</span>
            </div>
            <div data-index="05">
              <b>{formatCapacity(totalStats.capacityMt)}</b>
              <span>Total Capacity</span>
            </div>
          </div>

          <section className="mission-intro" id="legacy-about-mission">
            <div className="mission-copy">
              <div className="section-eyebrow">National Cooperative Storage Mission</div>
              <h3>From PACS registration to a fully operational modern grain storage facility</h3>
              <p>The PM Anna Bhandaran Yojana digital workflow brings approvals, MOU execution, survey, soil testing, design, DPR, tender, construction and subsidy monitoring onto one accountable national platform.</p>
            </div>
            <div className="mission-status">
              <div><b>{Number(publicSummary?.pacsApproved || 0).toLocaleString("en-IN")}</b><span>Approved PACS</span></div>
              <div><b>{liveActivities.length.toLocaleString("en-IN")}</b><span>Live Actions</span></div>
              <div><b>{liveTenders.length.toLocaleString("en-IN")}</b><span>Open Tenders</span></div>
              <div><b>{videoHighlights.length.toLocaleString("en-IN")}</b><span>Field Highlights</span></div>
            </div>
          </section>

          <div className="overview-title">
            <span>State-wise Programme Overview</span>
            <small>Live project footprint across India</small>
          </div>
          <div className="state-slider">
            <div className="state-track">
              {[...sliderData, ...sliderData].map((s, i) => <div className="state-card" key={`${s.state}-${i}`}>
                  <h4>{s.state}</h4>
                  <p>
                    <b>{Number(s.pacs || 0).toLocaleString("en-IN")}</b> PACS
                    Applied
                  </p>
                  <p>
                    <b>{Number(s.projects || 0).toLocaleString("en-IN")}</b>{" "}
                    Projects Started
                  </p>
                  <p>
                    <b>{formatCapacity(s.capacityMt)}</b> Capacity
                  </p>
                </div>)}
            </div>
          </div>

          <div className="command-title">
            <div><div className="section-eyebrow">National Command Centre</div><h3>Live Mission Intelligence</h3></div>
            <p>Verified data is updated automatically from authorised workflow actions</p>
          </div>

          <section className="highlight-section" id="legacy-highlights">
            <div className="highlight-head">
              <div>
                <h3>Project Video Highlights</h3>
                <p>Verified field progress, completed works and public programme stories</p>
              </div>
              <span>NAFED VERIFIED MEDIA</span>
            </div>
            <div className="highlight-grid">
              {videoHighlights.length ? videoHighlights.map((item) => {
    const embedUrl = getVideoEmbedUrl(item.videoUrl);
    return <article className="highlight-card" key={item.id}>
                      <div className="highlight-media">
                        {embedUrl ? <iframe
      src={embedUrl}
      title={item.title || "NAFED project highlight"}
      loading="lazy"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    /> : /\.(mp4|webm|ogg)(\?|$)/i.test(item.videoUrl) ? <video controls preload="none" poster={item.thumbnailUrl || ""}>
                            <source src={item.videoUrl} />
                          </video> : <a
      className="highlight-link"
      href={item.videoUrl}
      target="_blank"
      rel="noreferrer"
    >
                            ▶ Open verified video
                          </a>}
                      </div>
                      <div className="highlight-info">
                        <h4>{item.title || "Project Progress Highlight"}</h4>
                        <p>{item.description || "Verified NAFED programme field update."}</p>
                        <small>
                          {item.state || "National"} · {formatLiveDate(item.publishedAt)}
                        </small>
                      </div>
                    </article>;
  }) : <div className="highlight-empty">
                  Verified project video highlights will appear here automatically after publication.
                </div>}
            </div>
          </section>

          <section className="public-monitor" id="legacy-tenders">
            <article className="monitor-panel">
              <div className="monitor-head">
                <div>
                  <h3>Approval & Activity Monitor</h3>
                  <p>Latest authorised actions across the NAFED workflow</p>
                </div>
                <span
    className={`live-badge ${liveConnection === "LIVE" ? "" : "waiting"}`}
  >
                  {liveConnection === "LIVE" ? "● LIVE" : "PUBLIC FEED"}
                </span>
              </div>
              <div className="activity-list">
                {liveActivities.length ? liveActivities.slice(0, 8).map((item) => <div className="activity-row" key={item.id}>
                      <div className="activity-mark">✓</div>
                      <div>
                        <b>{item.title}</b>
                        <span>
                          {item.actor} · {item.state} · {item.type}
                        </span>
                      </div>
                      <time>{formatLiveDate(item.createdAt)}</time>
                    </div>) : <div className="empty-live">
                    Approved public activities will appear here automatically.
                  </div>}
              </div>
            </article>

            <article className="monitor-panel">
              <div className="monitor-head">
                <div>
                  <h3>Live Tender Notice Board</h3>
                  <p>Published NAFED grain-storage tenders and bid schedules</p>
                </div>
                <a className="all-tender-link" href="/tender/list?channel=NAFED">
                  View All Tenders
                </a>
              </div>
              <div className="tender-grid">
                {liveTenders.length ? liveTenders.slice(0, 4).map((tender) => <div className="tender-card" key={tender.id}>
                      <div className="tender-top">
                        <strong>{tender.tenderNo}</strong>
                        <span className="tender-status">{tender.status}</span>
                      </div>
                      <h4>{tender.title}</h4>
                      <div className="tender-meta">
                        <span>
                          State<b>{tender.state}</b>
                        </span>
                        <span>
                          Capacity<b>{formatCapacity(tender.capacity)}</b>
                        </span>
                        <span>
                          Tender Value<b>{formatMoney(tender.value)}</b>
                        </span>
                        <span>
                          Closing<b>{formatLiveDate(tender.closingDate)}</b>
                        </span>
                      </div>
                      <a
    className="tender-link"
    href={`/nafed/tenders/${tender.id}`}
  >
                        View Tender Details →
                      </a>
                    </div>) : <div className="empty-live" style={{ gridColumn: "1/-1" }}>
                    Published public tenders will appear here in real time.
                  </div>}
              </div>
            </article>
          </section>

          <div className="command-title">
            <div><div className="section-eyebrow">Public Services</div><h3>Quick Access</h3></div>
            <p>Official information, support and programme resources</p>
          </div>
          <section className="quick-links">
            <a className="quick-link" href="/nafed/guidelines"><i>📑</i><b>Scheme Guidelines</b><span>Official eligibility, process and implementation documents.</span><em>Open Guidelines →</em></a>
            <a className="quick-link" href="/tender/list?channel=NAFED"><i>📢</i><b>Tender Notice Board</b><span>Live tenders, bid schedules and procurement notices.</span><em>View Tenders →</em></a>
            <a className="quick-link" href="/nafed/help"><i>🎧</i><b>Mission Help Desk</b><span>Role support, registration assistance and grievance services.</span><em>Get Support →</em></a>
            <a className="quick-link" href="/nafed/contact"><i>🏛️</i><b>Contact NAFED</b><span>National and State programme coordination contacts.</span><em>Contact Us →</em></a>
          </section>
        </main>

        {showLoginPopup && <div className="overlay">
            <div className="modal" role="dialog" aria-modal="true" aria-labelledby="nafed-login-title">
              <div className="modal-head">
                <div>
                  <div
    style={{
      fontSize: 11,
      fontWeight: 950,
      letterSpacing: ".16em"
    }}
  >
                    NAFED ROLE LOGIN
                  </div>
                  <h2 id="nafed-login-title">
                    {loginMode === "ROLE" ? "Select Portal Role" : selectedRoleData[1]}
                  </h2>
                </div>
                <button
    type="button"
    className="modal-close"
    aria-label="Close login"
    onClick={() => setShowLoginPopup(false)}
  >
                  ×
                </button>
              </div>

              <div className="modal-body">
                {loginMode !== "ROLE" && <div className="role-badge"><PortalIcon name={roleIconName(selectedRole)} size={24} /></div>}
                <h2 className="modal-title">
                  {loginMode === "ROLE" && "Authorised Login"}
                  {loginMode === "PASSWORD" && `${selectedRoleData[1]} Login`}
                  {loginMode === "OTP" && "Login with OTP"}
                  {loginMode === "FORGOT" && "Forgot Password"}
                </h2>

                {loginMode === "ROLE" && <div className="login-role-grid">
                    {ROLE_OPTIONS.map(([value, label, icon]) => <button
    type="button"
    className="login-role-option"
    key={value}
    onClick={() => {
      setSelectedRole(value);
      setLoginMode("PASSWORD");
    }}
  >
                        <span><PortalIcon name={roleIconName(value)} size={20} /></span>
                        <b>{label}</b>
                        <i><PortalIcon name="chevron" size={15} /></i>
                      </button>)}
                  </div>}

                {loginMode === "PASSWORD" && <form onSubmit={handleLogin}>
                    <input
    className="input"
    type="email"
    aria-label="Registered email"
    autoComplete="username"
    placeholder="Enter email or username"
    value={email}
    onChange={(e) => setEmail(e.target.value)}
  />
                    <input
    className="input"
    type="password"
    aria-label="Password"
    autoComplete="current-password"
    placeholder="Enter password"
    value={password}
    onChange={(e) => setPassword(e.target.value)}
  />

                    <div className="login-row">
                      <label>
                        <input type="checkbox" /> Remember Me
                      </label>
                      <button
    type="button"
    className="link-btn"
    onClick={() => setLoginMode("FORGOT")}
  >
                        Forgot Password?
                      </button>
                    </div>

                    <button className="main-btn" disabled={loading}>
                      {loading ? "Please wait..." : <><PortalIcon name="lock" size={17} />Login</>}
                    </button>
                    <div className="or">OR</div>
                    <button
    type="button"
    className="main-btn outline-btn"
    onClick={() => setLoginMode("OTP")}
  >
                      <PortalIcon name="phone" size={17} />Login with OTP
                    </button>

                    <p
    style={{
      textAlign: "center",
      marginTop: 14,
      fontSize: 13
    }}
  >
                      New User?{" "}
                      <button
    type="button"
    className="link-btn"
    onClick={() => {
      setRole(selectedRole);
      setShowLoginPopup(false);
      setShowRegisterPopup(true);
    }}
  >
                        Register Here
                      </button>
                    </p>
                  </form>}

                {loginMode === "OTP" && <div>
                    <input
    className="input"
    type="text"
    aria-label="Registered mobile number"
    inputMode="numeric"
    autoComplete="tel-national"
    placeholder="10 digit mobile number"
    value={mobile}
    onChange={(e) => setMobile(
      e.target.value.replace(/\D/g, "").slice(0, 10)
    )}
  />
                    <button
    className="main-btn"
    onClick={sendOtp}
    disabled={loading}
  >
                      {loading ? "Please wait..." : "Send OTP"}
                    </button>
                    <input
    className="input"
    style={{ marginTop: 14 }}
    type="text"
    aria-label="One-time password"
    inputMode="numeric"
    autoComplete="one-time-code"
    placeholder="Enter OTP"
    value={otp}
    onChange={(e) => setOtp(e.target.value)}
  />
                    <button
    className="main-btn green-btn"
    onClick={verifyOtp}
    disabled={loading}
  >
                      Verify OTP
                    </button>
                    <div id="nafed-recaptcha" />
                    <p style={{ textAlign: "center", marginTop: 14 }}>
                      <button
    type="button"
    className="link-btn"
    onClick={() => setLoginMode("PASSWORD")}
  >
                        Back to Login
                      </button>
                    </p>
                  </div>}

                {loginMode === "FORGOT" && <div>
                    <input
    className="input"
    type="email"
    aria-label="Registered email for password reset"
    autoComplete="email"
    placeholder="Registered Email"
    value={email}
    onChange={(e) => setEmail(e.target.value)}
  />
                    <button
    className="main-btn"
    onClick={handleForgotPassword}
    disabled={loading}
  >
                      Send Reset Link
                    </button>
                    <p style={{ textAlign: "center", marginTop: 14 }}>
                      <button
    type="button"
    className="link-btn"
    onClick={() => setLoginMode("PASSWORD")}
  >
                        Back to Login
                      </button>
                    </p>
                  </div>}
              </div>
            </div>
          </div>}

        {showRegisterPopup && <div className="overlay">
            <div className="modal reg-modal" role="dialog" aria-modal="true" aria-labelledby="nafed-register-title">
              <div className="modal-head">
                <div>
                  <div
    style={{
      fontSize: 11,
      fontWeight: 950,
      letterSpacing: ".16em"
    }}
  >
                    NAFED UNIVERSAL REGISTRATION
                  </div>
                  <h2 id="nafed-register-title">Create New Portal Account</h2>
                </div>
                <button
    type="button"
    className="modal-close"
    aria-label="Close registration"
    onClick={() => setShowRegisterPopup(false)}
  >
                  ×
                </button>
              </div>

              <form className="modal-body" onSubmit={handleRegister}>
                <div className="reg-grid">
                  <div className="reg-field full">
                    <label htmlFor="nafed-register-role">User Role</label>
                    <select
    id="nafed-register-role"
    value={role}
    onChange={(e) => setRole(e.target.value)}
  >
                      {REGISTERABLE_ROLES.map(([value, label]) => <option key={value} value={value}>
                          {label}
                        </option>)}
                    </select>
                  </div>

                  <div className="role-scope-note reg-field full">
                    <b>{getRoleLabel(role)} Registration</b>
                    <span>
                      {role === "HEAD_OFFICE" && "Head Office is a National-level registration. No State, District, Taluka or Village selection is required."}
                      {role === "PACS" && "Complete PACS location is required: State, District, Taluka, Village and full address."}
                      {role === "PMC" && "PMC registration is State-specific. The same company may create a separate approved registration for each State using a separate authorised login email."}
                      {role === "STATE_BRANCH" && "State Branch jurisdiction is State-level; District, Taluka and Village are not required."}
                      {role === "STATE_REGISTRAR" && "State Registrar jurisdiction is State-level only."}
                      {role === "DISTRICT_REGISTRAR" && "District Registrar jurisdiction requires State and District selection."}
                      {role === "BANK" && "Bank registration requires State, District and complete branch address."}
                    </span>
                    <em>Approval Authority: {approvalConfig.approverLabel}</em>
                  </div>

                  <div className="reg-field">
                    <label htmlFor="nafed-register-name">Full Name</label>
                    <input
    id="nafed-register-name"
    type="text"
    placeholder="Full Name"
    value={name}
    onChange={(e) => setName(e.target.value)}
  />
                  </div>

                  <div className="reg-field">
                    <label htmlFor="nafed-register-organization">{identityConfig.organization}</label>
                    <input
    id="nafed-register-organization"
    type="text"
    placeholder={identityConfig.organization}
    value={organizationName}
    onChange={(e) => setOrganizationName(e.target.value)}
  />
                  </div>

                  <div className="reg-field">
                    <label htmlFor="nafed-register-registration-number">{identityConfig.registration}</label>
                    <input
    id="nafed-register-registration-number"
    type="text"
    placeholder={identityConfig.registration}
    value={registrationNumber}
    onChange={(e) => setRegistrationNumber(e.target.value.toUpperCase())}
  />
                  </div>

                  <div className="reg-field">
                    <label htmlFor="nafed-register-designation">{identityConfig.designation}</label>
                    <input
    id="nafed-register-designation"
    type="text"
    placeholder={identityConfig.designation}
    value={designation}
    onChange={(e) => setDesignation(e.target.value)}
  />
                  </div>

                  <div className="reg-field">
                    <label htmlFor="nafed-register-mobile">Mobile Number</label>
                    <input
    id="nafed-register-mobile"
    type="text"
    placeholder="10 digit mobile"
    value={mobile}
    onChange={(e) => setMobile(
      e.target.value.replace(/\D/g, "").slice(0, 10)
    )}
  />
                  </div>

                  <div className="reg-field">
                    <label htmlFor="nafed-register-email">Email</label>
                    <input
    id="nafed-register-email"
    type="email"
    placeholder="Email ID"
    value={email}
    onChange={(e) => setEmail(e.target.value)}
  />
                  </div>

                  <div className="reg-field">
                    <label htmlFor="nafed-register-password">Password</label>
                    <input
    id="nafed-register-password"
    type="password"
    placeholder="Create Password"
    value={password}
    onChange={(e) => setPassword(e.target.value)}
  />
                  </div>

                  <div className="reg-field">
                    <label htmlFor="nafed-register-confirm-password">Confirm Password</label>
                    <input
    id="nafed-register-confirm-password"
    type="password"
    placeholder="Re-enter Password"
    value={confirmPassword}
    onChange={(e) => setConfirmPassword(e.target.value)}
  />
                  </div>

                  {registrationConfig.state && <div className="reg-field">
                      <label htmlFor="nafed-register-state">State</label>
                      <select
    id="nafed-register-state"
    value={regState}
    onChange={(e) => setRegState(e.target.value)}
  >
                        <option value="">Select State</option>
                        {DEFAULT_STATE_STATS.map((item) => <option key={item.state} value={item.state}>
                            {item.state}
                          </option>)}
                      </select>
                    </div>}

                  {registrationConfig.district && <div className="reg-field">
                      <label htmlFor="nafed-register-district">District</label>
                      <select
    id="nafed-register-district"
    value={regDistrict}
    onChange={(e) => setRegDistrict(e.target.value)}
    disabled={districtLoading || !regState}
  >
                        <option value="">
                          {districtLoading ? "Loading Districts..." : "Select District"}
                        </option>
                        {districtOptions.map((districtName) => <option key={districtName} value={districtName}>
                            {districtName}
                          </option>)}
                      </select>
                    </div>}

                  {registrationConfig.taluka && <div className="reg-field">
                      <label htmlFor="nafed-register-taluka">Taluka / Block</label>
                      <input
    id="nafed-register-taluka"
    type="text"
    placeholder="Enter Taluka / Block manually"
    value={regTaluka}
    onChange={(e) => setRegTaluka(e.target.value)}
  />
                    </div>}

                  {registrationConfig.village && <div className="reg-field">
                      <label htmlFor="nafed-register-village">Village</label>
                      <input
    id="nafed-register-village"
    type="text"
    placeholder="Enter Village manually"
    value={regVillage}
    onChange={(e) => setRegVillage(e.target.value)}
  />
                    </div>}

                  {registrationConfig.address && <div className="reg-field full">
                      <label htmlFor="nafed-register-address">Full Address</label>
                      <input
    id="nafed-register-address"
    type="text"
    placeholder="Office address with PIN code"
    value={regAddress}
    onChange={(e) => setRegAddress(e.target.value)}
  />
                    </div>}
                </div>

                <div className="reg-note">
                  This registration will be submitted to{" "}
                  <b>{approvalConfig.approverLabel}</b>. Dashboard access will
                  remain locked until the higher authority approves and
                  activates the account.
                </div>

                <div className="reg-actions">
                  <button
    type="button"
    className="cancel-btn"
    onClick={() => setShowRegisterPopup(false)}
  >
                    Cancel
                  </button>
                  <button
    type="submit"
    className="submit-btn"
    disabled={loading}
  >
                    {loading ? "Submitting..." : "Submit Registration"}
                  </button>
                </div>
              </form>
            </div>
          </div>}

        <footer className="footer">
          <span>© 2024 NAFED. All Rights Reserved.</span>
          <span>Privacy Policy | Terms & Conditions</span>
          <span>Powered by Shine Agri Construction</span>
        </footer>
      </div>
    </>;
}
export {
  NafedHomePage as default
};
